import * as THREE from 'three'
import ThreeJsRenderer from './ThreeJsRenderer'
import FBXLoader from './loaders/FBXLoader'
import GLTFLoader from './loaders/GLTFLoader.js';
import Util from "./Util";
import CONST from "../const";
import {loadImage} from "../loadImageByPromise";
const base_path = /localhost/.test(location.href) ? '/' : process.env.VUE_APP_PATH;
import ColorThief from 'colorthief'

let silky_model;
let ladder_code_model;
let ladder_tape_model;
let line_drape_model;
let line_drape_pair_model_close;
let line_drape_pair_model_open;
let line_drape_wood_model;
let ar_model_close;
let ar_model_open;
let roll_screen_lark_model_exposure;
let roll_screen_lark_model_bag;
let roll_screen_lark_large_model;
let roll_screen_duole_model;
let roll_screen_duole_slim_model;
let zephyr_model_open;
let zephyr_model_half;
let zephyr_model_close;
let pelre_double_model_open;
let pelre_double_model_half;
let pelre_double_model_close;
let pelre_pair_model_open;
let pelre_pair_model_half;
let pelre_pair_model_close;
let pelre_model;
let lumie_model_close;
let lumie_model_open;

const DRAPE_OPACITY = 1;
const LACE_OPACITY = 0.8;
const isLace = (ctgId, attrib) => ctgId?.indexOf('lace') !== -1 || attrib === 'lace';

//transform_image.jsにて、Example8というクラス名でimportされてる。
class Example1 extends ThreeJsRenderer {
    async start(img_info) {
        const scene = new THREE.Scene();
        const camera = createCamera();
        scene.add(camera);

        //照明
        if(img_info.prefix === 'PS' || img_info.prefix === 'HS') {
            //プリーツスクリーンとハニカムスクリーンは上からも照らすため、
            //正面の照明を弱めておく。
            scene.add(createFrontLight(0.8));
            scene.add(createAmbientLight(img_info));
        } else {
            scene.add(createFrontLight(1));
        }
        const extra_light = createExtraLight(img_info);
        scene.add(extra_light);
        
        //カーテンの読み込み
        const curtain = await loadCurtain(img_info);

        const curtain_pivot = new THREE.Group();
        curtain_pivot.add(curtain);
        const bbox = new THREE.Box3().setFromObject(curtain);
        const obj_size = Util.calcObjectSizeFromBox3(bbox);

        this.renderer.setSize(img_info.width*1.005, img_info.height*1.005, false); //生成する画像サイズ
        scene.add(curtain_pivot);
        curtain.position.set(0, -obj_size.y/2, 0);
        curtain_pivot.scale.set(img_info.width/obj_size.x, img_info.height/obj_size.y, 15/obj_size.z); //画像内の製品サイズ

        const bbox_pivot = new THREE.Box3().setFromObject(curtain_pivot);
        const obj_size_pivot = Util.calcObjectSizeFromBox3(bbox_pivot);
        curtain_pivot.position.set(0, obj_size_pivot.y/2, -500);

        camera.far = 750;
        const render = () =>  {
            camera.right  = -this.canvas.width/2;
            camera.left   = this.canvas.width/2;
            camera.top    = -this.canvas.height/2;
            camera.bottom = this.canvas.height/2;
            camera.updateProjectionMatrix();

            this.renderer.render(scene, camera);
        };
        render();

        const url = this.canvas.toDataURL('image/bmp');
        const img = await new Promise(r => {
            const temp = new Image();
            temp.onload = () => r(temp);
            temp.src = url;
        });
        return img;
    }
}

export default Example1;

function createCamera() {
    const left   = 0;
    const right  = 0;
    const top    = 0;
    const bottom = 0;
    const near   = 1;
    const far    = 1500;
    const camera = new THREE.OrthographicCamera(left, right, top, bottom, near, far);
    camera.zoom = -1;
    camera.position.set(0, 0, 1);
    camera.lookAt(0,0,0);

    return camera;
}

async function loadCurtain(img_info) {
    switch (img_info.productType) {
        //ヨコ型ブラインド
        case 'PIB-25': //パーフェクトシルキー
        case 'IB-SHDY-25': //シルキーシェイディRDS
        case 'IB-RDS-25': //シルキーRDS
            return loadSilky(img_info);
        case 'AB-EXE-50': //アフタービートエグゼ（ラダーコード）
        case 'AB-50': //アフタービート（ラダーコード）
            return loadLadderCode(img_info);
        case 'AB-50-TAPE': //アフタービート（ラダーテープ）
            return loadLadderTape(img_info);

        //木製ブラインド
        case 'FT-EXE-CODE-50': //フォレティアエグゼ（ラダーコード）
        case 'FT-CODE-50': //フォレティア（ラダーコード）
        // case 'FT-CHAIN-CODE-50': //フォレティアチェーン（ラダーコード）
            return loadLadderCode(img_info);
        case 'FT-TAPE-50': //フォレティア（ラダーテープ）
            return loadLadderTape(img_info);

        //タテ型ブラインド
        case 'LD-100': //ラインドレープ
            return loadLineDrape(img_info);
        case 'LD-PE-100': //ラインドレープ ペアアンサンブル
            return loadLineDrapePair(img_info);
        case 'LD-WD-89': //ラインドレープ木製
            return loadLineDrapeWood(img_info);

        //調光タテ型ブラインド
        case 'AR': //エアレ
            return loadAR(img_info);

        //ロールスクリーン
        case 'RS': //ロールスクリーン ラルクシールド
            return loadRollScreenLark(img_info);
        case 'RS-Large': //ロールスクリーン ラルク大型
            return loadRollScreenLarkLarge(img_info);
        case 'DU': //調光ロールスクリーン デュオレ
            return loadRollScreenDuole(img_info);
        case 'DU-SLIM': //調光ロールスクリーン デュオレスリム
            return loadRollScreenDuoleSlim(img_info);
        case 'DU-LUMIE':
            return loadRollScreenLumie(img_info);

        //プリーツスクリーン、ハニカムスクリーン
        case 'ZF': //ゼファー
            return loadZephyr(img_info);
        case 'PL-DOUBLE': //ペルレ ダブル
            return loadPelreDouble(img_info);
        case 'ZF-FE-PAIR': //フィーユペア
        case 'PL-PAIR': //ペルレ ペア
        case 'HS-PAIR': //ブレア ペア
            return loadPelrePair(img_info);
        case 'ZF-FE': //フィーユ
        case 'PL': //ペルレ
        case 'HS': //ブレア
            return loadPelre(img_info);

        default: //仮データ
            return loadAssumedModel(img_info);
    }
}

function createExtraLight(img_info) {
    let is_closed_slat;
    switch (img_info.productType) {
        //ヨコ型ブラインド
        case 'PIB-25': //パーフェクトシルキー
        case 'IB-SHDY-25': //シルキーシェイディRDS
        case 'IB-RDS-25': //シルキーRDS
        //木製ブラインド
        case 'FT-EXE-CODE-50': //フォレティアエグゼ（ラダーコード）
        case 'FT-CODE-50': //フォレティア（ラダーコード）
        // case 'FT-CHAIN-CODE-50': //フォレティアチェーン（ラダーコード）
        case 'FT-TAPE-50': //フォレティア（ラダーテープ）
            return createLight(0, 1, -1, 1);

        case 'LD-PE-100': //ラインドレープ ペアアンサンブル
            is_closed_slat = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';
            if(is_closed_slat) {
                return createLight(1, 0, 0, 0.7);
            } else {
                return createLight(0.5, 0, 1, 1);
            }

        case 'ZF': //ゼファー
        case 'PL-DOUBLE': //ペルレ ダブル
        case 'ZF-FE-PAIR': //フィーユペア
        case 'PL-PAIR': //ペルレ ペア
        case 'HS-PAIR': //ブレア ペア
        case 'ZF-FE': //フィーユ
        case 'PL': //ペルレ
        case 'HS': //ブレア
            return createLight(0, 1, 1, 0.2);

        default:
            return createLight(-1, 0, -1, 1);
    }
}

function createFrontLight(intensity) {
    // X軸が左から右
    // Y軸が下から上
    // Z軸が奥から手前
    return createLight(0, 0, 1, intensity);
}

function createAmbientLight(img_info) {
    //ドレープの密度が高くなるにつれて暗くなるため、環境光源で明るくする。
    const real_height = Math.min(img_info.rect_height, 3500);
    const ratio = real_height / 10000;
    let intensity = ratio + ratio * ratio;

    //ゼファーは特にドレープの一山が小さいため明るさを強調する
    if (img_info.productType === 'ZF') {
        intensity += ratio * ratio + ratio * ratio;
    }

    return new THREE.AmbientLight(0xFFFFFF, intensity);
}

async function loadSilky(img_info) {
    console.groupCollapsed('loadCurtain');

    //開閉
    const is_closed_slat = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';

    if (!silky_model) {
        silky_model = await loadModel('model/2_シルキーRDS_ワンポール_スラット角度全閉.gltf');
    }
    const curtain = silky_model.scenes[0].clone();
    console.log(curtain);

    //設定値
    const slat_gap = 0.001;

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const bottom_mekas = curtain.getObjectByName('meka_2');
    const org_slat = curtain.getObjectByName('slat');

    //生地のテクスチャ
    const prefix = img_info.prefix ? `${img_info.prefix}/` : '';
    const slat_texture_url = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name}.jpg`;
    const slat_texture = await loadTexture(slat_texture_url);
    org_slat.material = createMaterial(slat_texture);

    //部品色のテクスチャ
    try{
        const box_texture = await loadTexture(`item_image/parts/${img_info.parts.box}.jpg`);
        const resin_texture = await loadTexture(`item_image/parts/${img_info.parts.resin}.jpg`);

        const box_material = createMaterial(box_texture);
        const resin_material = createMaterial(resin_texture);

        curtain.getObjectByName('headbox001').material = box_material;
        curtain.getObjectByName('bottomrail-R001').material = box_material;
        curtain.getObjectByName('boxcap-L001').material = resin_material;
        curtain.getObjectByName('headbox-R002').material = resin_material;
        curtain.getObjectByName('bottomcap-R001').material = resin_material;
        curtain.getObjectByName('bottomcap-L001').material = resin_material;
    } catch(e) {
        console.log(e);
    }

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const slat_height = getHeight(org_slat);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = img_info.slatWidth;
    //スラットのリピート回数を算出
    const real_top_mekas_height = top_mekas_height * (real_slat_height / slat_height);
    const real_bottom_mekas_height = bottom_mekas_height * (real_slat_height / slat_height);
    const repeat_num = Math.floor((real_product_height - real_top_mekas_height - real_bottom_mekas_height) / real_slat_height);
    console.log('repeat_num', repeat_num);

    //上部と下部の位置設定
    const fix_gap = 0.005;
    const slat_height_sum = (repeat_num * slat_height + slat_gap * (repeat_num - 1));
    const top_mekas_y_position = slat_height_sum / 2 + top_mekas_height - fix_gap;
    const bottom_mekas_y_position = -slat_height_sum / 2 + fix_gap;
    top_mekas.position.y = top_mekas_y_position;
    bottom_mekas.position.y = bottom_mekas_y_position;

    //スラットを複製して設置
    for(let i=0; i<repeat_num; i++) {
        const slat = org_slat.clone();
        slat.position.y = slat_height_sum / 2 - (slat_height + slat_gap) * i;

        if(is_closed_slat) {
            slat.rotation.z = getSlatRotation(0.007, i, repeat_num);
        } else {
            slat.rotation.x = 3.4; //全開にする
            slat.rotation.z = getSlatRotation(0.05, i, repeat_num);
        }
        curtain.add(slat);
    }

    //元のスラットを消す
    curtain.remove(org_slat);

    //背景を設置
    if(is_closed_slat) {
        curtain.add(await getPlaneWall(curtain, slat_texture_url));
    }

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadLadderCode(img_info) {
    console.groupCollapsed('loadCurtain');

    //開閉
    const is_closed_slat = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';

    if (!ladder_code_model) {
        ladder_code_model = await loadModel('model/4_フォレティアチェーン_ラダーコード仕様_スラット角度全閉.gltf');
    }
    const curtain = ladder_code_model.scenes[0].clone();
    console.log(curtain);

    //設定値
    const slat_gap = 0.003;
    const meka_posi_condition = 0.02;

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const bottom_mekas = curtain.getObjectByName('meka_2');
    const org_slat = curtain.getObjectByName('slat');

    //生地のテクスチャ
    const prefix = img_info.prefix ? `${img_info.prefix}/` : '';
    const slat_texture_url = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name}.jpg`;
    const slat_texture = await loadTexture(slat_texture_url);
    org_slat.material = createMaterial(slat_texture);

    //部品色のテクスチャ
    try{
        //アフタービート
        if(img_info.productType === 'AB-EXE-50' || img_info.productType === 'AB-50') {
            console.log('afterbeat');
            const bottom_rail_texture = await loadTexture(`item_image/parts/${img_info.parts.bottom_rail}.jpg`);
            const box_texture = await loadTexture(`item_image/parts/${img_info.parts.box}.jpg`);
            const boxcap_texture = await loadTexture(`item_image/parts/${img_info.parts.boxcap}.jpg`);

            curtain.getObjectByName('bottom-rail').material = createMaterial(bottom_rail_texture);
            curtain.getObjectByName('headbox004').material = createMaterial(bottom_rail_texture);
            curtain.getObjectByName('boxcap-R002').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('boxcap-L005').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('bottom-plate-L').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('bottom-plate-R').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('headbox-plate002').material = createMaterial(bottom_rail_texture);
        }
        //フォレティア
        else {
            const headbox_texture = await loadTexture(`item_image/parts/${img_info.parts.headbox}.jpg`);
            const boxcap_texture = await loadTexture(`item_image/parts/${img_info.parts.boxcap}.jpg`);

            curtain.getObjectByName('bottom-rail').material = createMaterial(headbox_texture);
            curtain.getObjectByName('headbox004').material = createMaterial(headbox_texture);
            curtain.getObjectByName('boxcap-R002').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('boxcap-L005').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('bottom-plate-L').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('bottom-plate-R').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('headbox-plate002').material = createMaterial(slat_texture);
        }
    } catch(e) {
        console.log(e);
    }

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const slat_height = getHeight(org_slat);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = img_info.slatWidth;
    //スラットのリピート回数を算出
    const real_top_mekas_height = top_mekas_height * (real_slat_height / slat_height);
    const real_bottom_mekas_height = bottom_mekas_height * (real_slat_height / slat_height);
    const repeat_num = Math.floor((real_product_height - real_top_mekas_height - real_bottom_mekas_height) / real_slat_height);
    console.log('repeat_num', repeat_num);

    //上部と下部の位置設定
    const slat_height_sum = (repeat_num * slat_height + slat_gap * (repeat_num - 1));
    const top_mekas_y_position = slat_height_sum / 2 + top_mekas_height - meka_posi_condition;
    const bottom_mekas_y_position = -slat_height_sum / 2 + meka_posi_condition;
    top_mekas.position.y = top_mekas_y_position;
    bottom_mekas.position.y = bottom_mekas_y_position;

    //スラットを複製して設置
    for(let i=0; i<repeat_num; i++) {
        const slat = org_slat.clone();
        slat.position.y = slat_height_sum / 2 - (slat_height + slat_gap) * i;

        if(is_closed_slat) {
            slat.rotation.z = getSlatRotation(0.02, i, repeat_num);
        } else {
            slat.rotation.x = 3.4; //全開にする
            slat.rotation.z = getSlatRotation(0.07, i, repeat_num);
        }
        curtain.add(slat);
    }

    //元のスラットを消す
    curtain.remove(org_slat);

    //背景を設置
    if(is_closed_slat) {
        curtain.add(await getPlaneWall(curtain, slat_texture_url, meka_posi_condition));
    }

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadLadderTape(img_info) {
    console.groupCollapsed('loadCurtain');

    //開閉
    const is_closed_slat = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';

    if (!ladder_tape_model) {
        ladder_tape_model = await loadModel('model/６_フォレティアチェーン_ラダーテープ仕様_スラット角度全閉.gltf');
    }
    const curtain = ladder_tape_model.scenes[0].clone();
    console.log(curtain);

    //設定値
    const slat_gap = 0;
    // const slat_gap = 0.001;
    const meka_posi_condition = 0.02;
    const assumed_default_width = 1000;

    //各3Dオブジェクト
    const tape = curtain.getObjectByName('tape');
    const top_mekas = curtain.getObjectByName('meka');
    const bottom_mekas = curtain.getObjectByName('meka_2');
    const org_slat = curtain.getObjectByName('Box001');

    //ユーザーが入力した製品幅mm
    const real_product_width = img_info.rect_width;
    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = img_info.slatWidth;

    //上部と下部のスケールを変更
    const scale_ratio = real_product_width / assumed_default_width;
    top_mekas.scale.x = scale_ratio;
    // bottom_mekas.scale.x = scale_ratio;
    //下部はレールだけ横に伸ばす
    bottom_mekas.getObjectByName('bottom-rail003').scale.y = scale_ratio;
    bottom_mekas.remove(bottom_mekas.getObjectByName('bottom-plate-L003'));
    bottom_mekas.remove(bottom_mekas.getObjectByName('bottom-plate-R003'));
    //スラットも横幅修正
    org_slat.scale.x = scale_ratio;

    const bottom_meka_left = bottom_mekas.getObjectByName('LayerGR2_012');
    const bottom_meka_center = bottom_mekas.getObjectByName('LayerGR2_011');
    const bottom_meka_right = bottom_mekas.getObjectByName('LayerGR2_010');
    const tape_left = tape.getObjectByName('Plane002');
    const tape_center = tape.getObjectByName('Plane001');
    const tape_right = tape.getObjectByName('Plane003');
    const tape_gap = new THREE.Box3().setFromObject(tape_center).min.x - new THREE.Box3().setFromObject(tape_left).min.x;

    //製品幅に合わせて、ラダーテープの本数と位置を調整
    //デフォルトはテープ3本
    if(real_product_width <= 800) {
        //テープ2本
        tape.remove(tape_right);
        tape_center.position.x = slideRight(tape_center, tape_gap);
        tape_left.position.x = slideRight(tape_left, tape_gap);
        bottom_mekas.remove(bottom_meka_right);
        bottom_meka_center.position.x = slideRight(bottom_meka_center, tape_gap);
        bottom_meka_left.position.x = slideRight(bottom_meka_left, tape_gap);
    } else if(real_product_width >= 1301 && real_product_width <= 1600) {
        //テープ4本
        const tape_most_left = tape_left.clone();
        tape_most_left.position.x -= tape_gap;
        tape_most_left.position.x = slideRight(tape_most_left, tape_gap);
        tape.add(tape_most_left);

        tape_center.position.x = slideRight(tape_center, tape_gap);
        tape_left.position.x = slideRight(tape_left, tape_gap);
        tape_right.position.x = slideRight(tape_right, tape_gap);

        const bottom_meka_most_left = bottom_meka_left.clone();
        bottom_meka_most_left.position.x -= tape_gap;
        bottom_meka_most_left.position.x = slideRight(bottom_meka_most_left, tape_gap);
        bottom_mekas.add(bottom_meka_most_left);

        bottom_meka_center.position.x = slideRight(bottom_meka_center, tape_gap);
        bottom_meka_left.position.x = slideRight(bottom_meka_left, tape_gap);
        bottom_meka_right.position.x = slideRight(bottom_meka_right, tape_gap);
    } else if(real_product_width >= 1601) {
        //テープ5本
        const tape_most_left = tape_left.clone();
        tape_most_left.position.x -= tape_gap;
        tape.add(tape_most_left);
        const tape_most_right = tape_right.clone();
        tape_most_right.position.x += tape_gap;
        tape.add(tape_most_right);

        const bottom_meka_most_left = bottom_meka_left.clone();
        bottom_meka_most_left.position.x -= tape_gap;
        bottom_mekas.add(bottom_meka_most_left);
        const bottom_meka_most_right = bottom_meka_right.clone();
        bottom_meka_most_right.position.x += tape_gap;
        bottom_mekas.add(bottom_meka_most_right);
    }

    //生地のテクスチャ
    const prefix = img_info.prefix ? `${img_info.prefix}/` : '';
    const slat_texture_url = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name}.jpg`;
    const slat_texture = await loadTexture(slat_texture_url);
    org_slat.material = createMaterial(slat_texture);

    //部品色のテクスチャ
    try{
        //アフタービート
        if(img_info.productType === 'AB-50-TAPE') {
            const bottom_rail_texture = await loadTexture(`item_image/parts/${img_info.parts.bottom_rail}.jpg`);
            const box_texture = await loadTexture(`item_image/parts/${img_info.parts.box}.jpg`);
            const boxcap_texture = await loadTexture(`item_image/parts/${img_info.parts.boxcap}.jpg`);
            const tape_texture = await loadTexture(`item_image/parts/${img_info.parts.tape}.jpg`);

            curtain.getObjectByName('bottom-rail003').material = createMaterial(bottom_rail_texture);
            curtain.getObjectByName('headbox005').material = createMaterial(bottom_rail_texture);
            curtain.getObjectByName('boxcap-R003').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('boxcap-L006').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('headbox-plate003').material = createMaterial(bottom_rail_texture);
            tape.children.forEach(mesh => mesh.material = createMaterial(tape_texture));
        }
        //フォレティア
        else {
            const headbox_texture = await loadTexture(`item_image/parts/${img_info.parts.headbox}.jpg`);
            const boxcap_texture = await loadTexture(`item_image/parts/${img_info.parts.boxcap}.jpg`);
            const tape_texture = await loadTexture(`item_image/parts/${img_info.parts.tape}.jpg`);

            curtain.getObjectByName('bottom-rail003').material = createMaterial(headbox_texture);
            curtain.getObjectByName('headbox005').material = createMaterial(headbox_texture);
            curtain.getObjectByName('boxcap-R003').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('boxcap-L006').material = createMaterial(boxcap_texture);
            curtain.getObjectByName('headbox-plate003').material = createMaterial(slat_texture);
            tape.children.forEach(mesh => mesh.material = createMaterial(tape_texture));
        }
    } catch(e) {
        console.log(e);
    }

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const slat_height = getHeight(org_slat);

    //スラットのリピート回数を算出
    const real_top_mekas_height = top_mekas_height * (real_slat_height / slat_height);
    const real_bottom_mekas_height = bottom_mekas_height * (real_slat_height / slat_height);
    const repeat_num = Math.floor((real_product_height - real_top_mekas_height - real_bottom_mekas_height) / real_slat_height);
    console.log('repeat_num', repeat_num);

    //上部と下部の位置設定
    const slat_height_sum = (repeat_num * slat_height + slat_gap * (repeat_num - 1));
    const top_mekas_y_position = slat_height_sum / 2 + top_mekas_height - meka_posi_condition;
    const bottom_mekas_y_position = -slat_height_sum / 2 + meka_posi_condition;
    top_mekas.position.y = top_mekas_y_position;
    bottom_mekas.position.y = bottom_mekas_y_position;

    //ラダーテープの縦の長さも合わせる
    const top_meka_condition = 0.05;
    const bottom_meka_condition = 0.002;
    let bbox_tape = new THREE.Box3().setFromObject(tape);
    const org_tape_length = bbox_tape.max.y - bbox_tape.min.y;
    const bbox_top_mekas = new THREE.Box3().setFromObject(top_mekas);
    const bbox_bottom_mekas = new THREE.Box3().setFromObject(bottom_mekas);
    const tape_length = bbox_top_mekas.max.y - bbox_bottom_mekas.min.y - top_meka_condition - bottom_meka_condition;
    tape.scale.y = tape_length / org_tape_length;
    bbox_tape = new THREE.Box3().setFromObject(tape);
    tape.position.y = tape.position.y + (bbox_top_mekas.max.y - bbox_tape.max.y) - top_meka_condition + bottom_meka_condition;

    //スラットを複製して設置
    for(let i=0; i<repeat_num; i++) {
        const slat = org_slat.clone();
        slat.position.y = slat_height_sum / 2 - (slat_height + slat_gap) * i;

        if(is_closed_slat) {
            slat.rotation.x = slat.rotation.x + getSlatRotation(0.13, i, repeat_num);
        } else {
            slat.rotation.x = 3.4 + getSlatRotation(0.07, i, repeat_num); //全開にする
        }
        curtain.add(slat);
    }

    //元のスラットを消す
    curtain.remove(org_slat);

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

//ドレープの全開・中間・全閉で、読み込む処理を変える。
function loadZephyr(img_info) {
    const drape_state = img_info.feature.value;
    if(drape_state === 'open') {
        return loadZephyrOpen(img_info);
    } else if(drape_state === 'close') {
        return loadZephyrClose(img_info)
    } else {
        return loadZephyrHalf(img_info)
    }
}
function loadPelreDouble(img_info) {
    const drape_state = img_info.feature.value;
    if(drape_state === 'open') {
        return loadPelreDoubleOpen(img_info);
    } else if(drape_state === 'close') {
        return loadPelreDoubleClose(img_info)
    } else {
        return loadPelreDoubleHalf(img_info)
    }
}
function loadPelrePair(img_info) {
    const drape_state = img_info.feature.value;
    if(drape_state === 'open') {
        return loadPelrePairOpen(img_info);
    } else if(drape_state === 'close') {
        return loadPelrePairClose(img_info)
    } else {
        return loadPelrePairHalf(img_info)
    }
}

async function loadZephyrOpen(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!zephyr_model_open) {
        zephyr_model_open = await loadModel('model/22_ゼファー_ワンチェーン操作_ドレープ全開.gltf');
    }
    const curtain = zephyr_model_open.scenes[0].clone();
    console.log(curtain);

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const center_mekas = curtain.getObjectByName('meka_2');
    const bottom_mekas = curtain.getObjectByName('meka_3');
    // const top_slat = curtain.getObjectByName('slat');
    const bottom_slat = curtain.getObjectByName('slat_2');
    // const top_last_slats = curtain.getObjectByName('slat_3');
    const center_meka_slat = curtain.getObjectByName('slat_4');
    const bottom_meka_slat = curtain.getObjectByName('slat_5');
    const slat_tamari = curtain.getObjectByName('slat_tamari');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const center_mekas_height = getHeight(center_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const bottom_slat_height = getHeight(bottom_slat);
    const center_meka_slat_height = getHeight(center_meka_slat);
    const bottom_meka_slat_height = getHeight(bottom_meka_slat);
    const slat_tamari_height = getHeight(slat_tamari);

    //ユーザーが入力した製品高mm（ブラウザの入力値から取得）
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 18;
    //実際の高さを算出するための比率
    const real_scale = real_slat_height / bottom_slat_height;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 36;

    //下部スラットのリピート回数を算出
    const real_bottom_slats_height_sum = real_product_height - (top_mekas_height + slat_tamari_height + center_mekas_height + bottom_mekas_height) * real_scale;
    const repeat_bottom_num = Math.floor(real_bottom_slats_height_sum / real_slat_height);
    console.log('repeat_bottom_num', repeat_bottom_num);

    //-------------------------------ここからがメインの処理-------------------------------

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    center_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    bottom_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));

    //テクスチャの縦分割数
    const tamari_num = 2; //スラット2本分くらいの大きさ
    const slat_height_ratio = 1 / (tamari_num + repeat_bottom_num); //全体に対する大きさの比率
    const top_texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    const bottom_texture_repeat_num = Math.floor(img_info.item_data.height_back / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const top_texture_arr = await createTextureArr(img_info, slat_height_ratio, top_texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    const bottom_texture_arr = await createTextureArr(img_info, slat_height_ratio, bottom_texture_repeat_num, img_info.item_data.name_back, img_info.item_data.width_back);
    //透明度の取得
    const top_opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;
    const bottom_opacity = isLace(img_info.item_data.ctgId_back, img_info.item_data.attrib_back) ? LACE_OPACITY : DRAPE_OPACITY;

    //スラット以外の位置設定
    //高さゼロ地点から各パーツを下に並べていって、最後に全体を中央に移動させる。
    const ex_height = 0.02; //高さ調整
    const ex_height_2 = 0.008;
    top_mekas.position.y = - top_mekas_height / 2;
    slat_tamari.position.y = - top_mekas_height - slat_tamari_height / 2;
    const center_mekas_position = - top_mekas_height - slat_tamari_height;
    center_mekas.position.y = center_mekas_position - center_mekas_height / 2;
    center_meka_slat.position.y = center_mekas_position - center_meka_slat_height / 2 + ex_height;

    const bottom_mekas_position = center_mekas_position - bottom_slat_height * repeat_bottom_num;
    bottom_mekas.position.y = bottom_mekas_position - center_mekas_height - bottom_mekas_height / 2 + ex_height_2;
    bottom_meka_slat.position.y = bottom_mekas_position - center_mekas_height - bottom_meka_slat_height / 2 + ex_height + ex_height_2;

    //上部スラットを複製して設置
    let i_top = 0;
    slat_tamari.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);
    center_meka_slat.material = createMaterialWithCanvas(top_texture_arr[++i_top % top_texture_repeat_num], top_opacity);

    //下部スラットを複製して設置
    let i_bottom = 0;
    for(i_bottom; i_bottom<repeat_bottom_num; i_bottom++) {
        const slat = bottom_slat.clone();
        slat.position.y = center_mekas_position - center_mekas_height - bottom_slat_height * i_bottom - bottom_slat_height / 2 + ex_height_2;
        slat.material = createMaterialWithCanvas(bottom_texture_arr[i_bottom % bottom_texture_repeat_num], bottom_opacity);
        curtain.add(slat);
    }
    curtain.remove(bottom_slat);
    bottom_meka_slat.material = createMaterialWithCanvas(bottom_texture_arr[i_bottom % bottom_texture_repeat_num], bottom_opacity);

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadZephyrClose(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!zephyr_model_close) {
        zephyr_model_close = await loadModel('model/22_ゼファー_ワンチェーン操作_ドレープ全閉.gltf');
    }
    const curtain = zephyr_model_close.scenes[0].clone();
    console.log(curtain);

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const center_mekas = curtain.getObjectByName('meka_2');
    // const bottom_mekas = curtain.getObjectByName('meka_3');
    const top_slat = curtain.getObjectByName('slat');
    // const bottom_slat = curtain.getObjectByName('slat_2');
    // const top_last_slats = curtain.getObjectByName('slat_3');
    const center_meka_slat = curtain.getObjectByName('slat_4');
    // const bottom_meka_slat = curtain.getObjectByName('slat_5');
    // const slat_tamari = curtain.getObjectByName('slat_tamari');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const center_mekas_height = getHeight(center_mekas);
    const top_slat_height = getHeight(top_slat);
    const center_meka_slat_height = getHeight(center_meka_slat);

    //ユーザーが入力した製品高mm（ブラウザの入力値から取得）
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 18;
    //実際の高さを算出するための比率
    const real_scale = real_slat_height / top_slat_height;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 36;

    //スラットのリピート回数を算出
    const real_slats_height_sum = real_product_height - (top_mekas_height + center_mekas_height) * real_scale;
    const repeat_num = Math.floor(real_slats_height_sum / real_slat_height);
    console.log('repeat_num', repeat_num);

    //-------------------------------ここからがメインの処理-------------------------------

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    center_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));

    //テクスチャの縦分割数
    const slat_height_ratio = 1 / repeat_num; //全体に対する大きさの比率
    const top_texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    const bottom_texture_repeat_num = Math.floor(img_info.item_data.height_back / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const top_texture_arr = await createTextureArr(img_info, slat_height_ratio, top_texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    //透明度の取得
    const top_opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;

    //スラット以外の位置設定
    //高さゼロ地点から各パーツを下に並べていって、最後に全体を中央に移動させる。
    const ex_height = 0.02; //高さ調整
    const ex_height_2 = 0.008;
    const ex_height_3 = 0.003;
    top_mekas.position.y = - top_mekas_height / 2;
    center_mekas.position.y = - top_mekas_height - top_slat_height * repeat_num - center_mekas_height / 2 + ex_height_3;
    center_meka_slat.position.y = - top_mekas_height - top_slat_height * repeat_num - center_meka_slat_height / 2 + ex_height + ex_height_3;

    //上部スラットを複製して設置
    let i_top = 0;
    for(i_top; i_top<repeat_num; i_top++) {
        const slat = top_slat.clone();
        slat.position.y = - top_mekas_height - top_slat_height * i_top - top_slat_height / 2 + ex_height_2;
        slat.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);
        curtain.add(slat);
    }
    curtain.remove(top_slat);
    center_meka_slat.material = createMaterialWithCanvas(top_texture_arr[++i_top % top_texture_repeat_num], top_opacity);

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadZephyrHalf(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!zephyr_model_half) {
        zephyr_model_half = await loadModel('model/19_ゼファー.gltf');
    }
    const curtain = zephyr_model_half.scenes[0].clone();
    console.log(curtain);


    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const center_mekas = curtain.getObjectByName('meka_2');
    const bottom_mekas = curtain.getObjectByName('meka_3');
    const top_slat = curtain.getObjectByName('slat');
    const bottom_slat = curtain.getObjectByName('slat_2');
    const top_last_slats = curtain.getObjectByName('slat_3');
    const center_meka_slat = curtain.getObjectByName('slat_4');
    const bottom_meka_slat = curtain.getObjectByName('slat_5');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const center_mekas_height = getHeight(center_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const top_slat_height = getHeight(top_slat);
    const bottom_slat_height = getHeight(bottom_slat);
    const top_last_slats_height = getHeight(top_last_slats);
    const center_meka_slat_height = getHeight(center_meka_slat);
    const bottom_meka_slat_height = getHeight(bottom_meka_slat);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    const real_product_height_top = real_product_height / 10 * 6;
    const real_product_height_bottom = real_product_height / 10 * 4;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 18;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 36;

    //上部スラットのリピート回数を算出
    const real_scale = real_slat_height / top_slat_height;
    const real_top_mekas_height = top_mekas_height * real_scale;
    const real_center_mekas_height = center_mekas_height * real_scale;
    const real_top_last_slats_height = top_last_slats_height * real_scale;
    const repeat_top_num = Math.floor((real_product_height_top - real_top_mekas_height - real_center_mekas_height - real_top_last_slats_height) / real_slat_height);
    console.log('repeat_top_num', repeat_top_num);
    //下部スラットのリピート回数を算出
    const real_bottom_mekas_height = bottom_mekas_height * real_scale;
    const repeat_bottom_num = Math.floor((real_product_height_bottom - real_bottom_mekas_height) / real_slat_height);
    console.log('repeat_bottom_num', repeat_bottom_num);

    // スラット以外の位置設定
    const ex_height = 0.023; //無理やり高さ調整した
    top_mekas.position.y = - top_mekas_height / 2;
    top_last_slats.position.y = top_last_slats_height - top_mekas_height - (top_slat_height * repeat_top_num) - ex_height;
    const center_mekas_position = - top_mekas_height - (top_slat_height * repeat_top_num) - top_last_slats_height - ex_height;
    center_mekas.position.y = center_mekas_height / 2 + center_mekas_position;
    center_meka_slat.position.y = center_meka_slat_height / 2 + center_mekas_position + 0.02;

    const bottom_mekas_position = center_mekas_position - (bottom_slat_height * repeat_bottom_num);
    bottom_mekas.position.y = - bottom_mekas_height / 2 + bottom_mekas_position;
    bottom_meka_slat.position.y = - bottom_meka_slat_height / 2 + bottom_mekas_position + 0.02;

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    bottom_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    center_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));


    //テクスチャの縦分割数
    const slat_height_ratio = 1 / (repeat_top_num + repeat_bottom_num); //全体に対する大きさの比率
    const top_texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    const bottom_texture_repeat_num = Math.floor(img_info.item_data.height_back / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const top_texture_arr = await createTextureArr(img_info, slat_height_ratio, top_texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    const bottom_texture_arr = await createTextureArr(img_info, slat_height_ratio, bottom_texture_repeat_num, img_info.item_data.name_back, img_info.item_data.width_back);
    //透明度の取得
    const top_opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;
    const bottom_opacity = isLace(img_info.item_data.ctgId_back, img_info.item_data.attrib_back) ? LACE_OPACITY : DRAPE_OPACITY;

    //上部スラットを複製して設置
    let i_top = 0;
    for(i_top; i_top<repeat_top_num; i_top++) {
        const slat = top_slat.clone();
        slat.position.y = - (top_slat_height / 2) - top_mekas_height - (top_slat_height * i_top);
        slat.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);

        curtain.add(slat);
    }
    top_last_slats.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);
    center_meka_slat.material = createMaterialWithCanvas(top_texture_arr[++i_top % top_texture_repeat_num], top_opacity);
    curtain.remove(top_slat);

    //下部スラットを複製して設置
    let i_bottom = 0;
    for(i_bottom; i_bottom<repeat_bottom_num; i_bottom++) {
        const slat = bottom_slat.clone();
        slat.position.y = - (bottom_slat_height / 2) + center_mekas_position - (top_slat_height * i_bottom);
        slat.material = createMaterialWithCanvas(bottom_texture_arr[i_bottom % bottom_texture_repeat_num], bottom_opacity);

        curtain.add(slat);
    }
    bottom_meka_slat.material = createMaterialWithCanvas(bottom_texture_arr[i_bottom % bottom_texture_repeat_num], bottom_opacity);
    curtain.remove(bottom_slat);


    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadPelreDoubleOpen(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!pelre_double_model_open) {
        pelre_double_model_open = await loadModel('model/23_ペルレダブル_ハンドル操作_ドレープ全開.gltf');
    }
    const curtain = pelre_double_model_open.scenes[0].clone();
    console.log(curtain);

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const center_mekas = curtain.getObjectByName('meka_2');
    const bottom_mekas = curtain.getObjectByName('meka_3');
    // const top_slat = curtain.getObjectByName('slat');
    const bottom_slat = curtain.getObjectByName('slat_2');
    // const top_last_slats = curtain.getObjectByName('slat_3');
    const slat_tamari = curtain.getObjectByName('slat_tamari');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const center_mekas_height = getHeight(center_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const bottom_slat_height = getHeight(bottom_slat);
    const slat_tamari_height = getHeight(slat_tamari);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 35;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 50;
    //実際の高さを算出するための比率
    const real_scale = real_slat_height / bottom_slat_height;

    //下部スラットのリピート回数を算出
    const real_bottom_slats_height_sum = real_product_height - (top_mekas_height + slat_tamari_height + center_mekas_height + bottom_mekas_height) * real_scale;
    const repeat_bottom_num = Math.floor(real_bottom_slats_height_sum / real_slat_height);
    console.log('repeat_bottom_num', repeat_bottom_num);

    //-------------------------------ここからがメインの処理-------------------------------

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    center_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    bottom_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));

    //テクスチャの縦分割数
    const tamari_num = 3;
    const slat_height_ratio = 1 / (tamari_num + repeat_bottom_num); //全体に対する大きさの比率
    const top_texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    const bottom_texture_repeat_num = Math.floor(img_info.item_data.height_back / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const top_texture_arr = await createTextureArr(img_info, slat_height_ratio, top_texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    const bottom_texture_arr = await createTextureArr(img_info, slat_height_ratio, bottom_texture_repeat_num, img_info.item_data.name_back, img_info.item_data.width_back);
    //透明度の取得
    const top_opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;
    const bottom_opacity = isLace(img_info.item_data.ctgId_back, img_info.item_data.attrib_back) ? LACE_OPACITY : DRAPE_OPACITY;

    //スラット以外の位置設定
    //高さゼロ地点から各パーツを下に並べていって、最後に全体を中央に移動させる。
    const ex_height_2 = 0.008; //高さ調整
    top_mekas.position.y = - top_mekas_height / 2;
    slat_tamari.position.y = - top_mekas_height - slat_tamari_height / 2;
    const center_mekas_position = - top_mekas_height - slat_tamari_height;
    center_mekas.position.y = center_mekas_position - center_mekas_height / 2;

    const bottom_mekas_position = center_mekas_position - bottom_slat_height * repeat_bottom_num;
    bottom_mekas.position.y = bottom_mekas_position - center_mekas_height - bottom_mekas_height / 2 + ex_height_2;

    //上部スラットを複製して設置
    let i_top = 0;
    slat_tamari.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);

    //下部スラットを複製して設置
    let i_bottom = 0;
    for(i_bottom; i_bottom<repeat_bottom_num; i_bottom++) {
        const slat = bottom_slat.clone();
        slat.position.y = center_mekas_position - center_mekas_height - bottom_slat_height * i_bottom - bottom_slat_height / 2 + ex_height_2;
        slat.material = createMaterialWithCanvas(bottom_texture_arr[i_bottom % bottom_texture_repeat_num], bottom_opacity);
        curtain.add(slat);
    }
    curtain.remove(bottom_slat);

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadPelreDoubleClose(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!pelre_double_model_close) {
        pelre_double_model_close = await loadModel('model/23_ペルレダブル_ハンドル操作_ドレープ全閉.gltf');
    }
    const curtain = pelre_double_model_close.scenes[0].clone();
    console.log(curtain);

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const center_mekas = curtain.getObjectByName('meka_2');
    // const bottom_mekas = curtain.getObjectByName('meka_3');
    const top_slat = curtain.getObjectByName('slat');
    // const bottom_slat = curtain.getObjectByName('slat_2');
    // const top_last_slats = curtain.getObjectByName('slat_3');
    // const slat_tamari = curtain.getObjectByName('slat_tamari');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const center_mekas_height = getHeight(center_mekas);
    const top_slat_height = getHeight(top_slat);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 35;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 50;
    //実際の高さを算出するための比率
    const real_scale = real_slat_height / top_slat_height;

    //スラットのリピート回数を算出
    const real_slats_height_sum = real_product_height - (top_mekas_height + center_mekas_height) * real_scale;
    const repeat_num = Math.floor(real_slats_height_sum / real_slat_height);
    console.log('repeat_num', repeat_num);

    //-------------------------------ここからがメインの処理-------------------------------

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    center_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));

    //テクスチャの縦分割数
    const slat_height_ratio = 1 / repeat_num; //全体に対する大きさの比率
    const top_texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const top_texture_arr = await createTextureArr(img_info, slat_height_ratio, top_texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    //透明度の取得
    const top_opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;

    //スラット以外の位置設定
    //高さゼロ地点から各パーツを下に並べていって、最後に全体を中央に移動させる。
    const ex_height_2 = 0.008; //高さ調整
    top_mekas.position.y = - top_mekas_height / 2;
    center_mekas.position.y = - top_mekas_height - top_slat_height * repeat_num - center_mekas_height / 2 + ex_height_2;

    //上部スラットを複製して設置
    let i_top = 0;
    for(i_top; i_top<repeat_num; i_top++) {
        const slat = top_slat.clone();
        slat.position.y = - top_mekas_height - top_slat_height * i_top - top_slat_height / 2 + ex_height_2;
        slat.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);
        curtain.add(slat);
    }
    curtain.remove(top_slat);

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadPelreDoubleHalf(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!pelre_double_model_half) {
        pelre_double_model_half = await loadModel('model/20_ペルレダブル.gltf');
    }
    const curtain = pelre_double_model_half.scenes[0].clone();
    console.log(curtain);


    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const center_mekas = curtain.getObjectByName('meka_2');
    const bottom_mekas = curtain.getObjectByName('meka_3');
    const top_slat = curtain.getObjectByName('slat');
    const bottom_slat = curtain.getObjectByName('slat_2');
    const top_last_slats = curtain.getObjectByName('slat_3');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const center_mekas_height = getHeight(center_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const top_slat_height = getHeight(top_slat);
    const bottom_slat_height = getHeight(bottom_slat);
    const top_last_slats_height = getHeight(top_last_slats);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    const real_product_height_top = real_product_height / 10 * 6;
    const real_product_height_bottom = real_product_height / 10 * 4;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 35;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 50;

    //上部スラットのリピート回数を算出
    const real_scale = real_slat_height / top_slat_height;
    const real_top_mekas_height = top_mekas_height * real_scale;
    const real_center_mekas_height = center_mekas_height * real_scale;
    const real_top_last_slats_height = top_last_slats_height * real_scale;
    const repeat_top_num = Math.floor((real_product_height_top - real_top_mekas_height - real_center_mekas_height - real_top_last_slats_height) / real_slat_height);
    console.log('repeat_top_num', repeat_top_num);
    //下部スラットのリピート回数を算出
    const real_bottom_mekas_height = bottom_mekas_height * real_scale;
    const repeat_bottom_num = Math.floor((real_product_height_bottom - real_bottom_mekas_height) / real_slat_height);
    console.log('repeat_bottom_num', repeat_bottom_num);

    // スラット以外の位置設定
    const ex_height = 0.02; //無理やり高さ調整した
    const ex_height_2 = 0.013;
    top_mekas.position.y = - top_mekas_height / 2;
    top_last_slats.position.y = top_last_slats_height - top_mekas_height - (top_slat_height * repeat_top_num) - ex_height;
    const center_mekas_position = - top_mekas_height - (top_slat_height * repeat_top_num) - top_last_slats_height - ex_height_2;
    center_mekas.position.y = center_mekas_height / 2 + center_mekas_position;

    const bottom_mekas_position = center_mekas_position - (bottom_slat_height * repeat_bottom_num);
    bottom_mekas.position.y = - bottom_mekas_height / 2 + bottom_mekas_position;

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    bottom_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    center_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));


    //テクスチャの縦分割数
    const slat_height_ratio = 1 / (repeat_top_num + repeat_bottom_num); //全体に対する大きさの比率
    const top_texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    const bottom_texture_repeat_num = Math.floor(img_info.item_data.height_back / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const top_texture_arr = await createTextureArr(img_info, slat_height_ratio, top_texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    const bottom_texture_arr = await createTextureArr(img_info, slat_height_ratio, bottom_texture_repeat_num, img_info.item_data.name_back, img_info.item_data.width_back);
    //透明度の取得
    const top_opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;
    const bottom_opacity = isLace(img_info.item_data.ctgId_back, img_info.item_data.attrib_back) ? LACE_OPACITY : DRAPE_OPACITY;

    //上部スラットを複製して設置
    let i_top = 0;
    for(i_top; i_top<repeat_top_num; i_top++) {
        const slat = top_slat.clone();
        slat.position.y = - (top_slat_height / 2) - top_mekas_height - (top_slat_height * i_top);
        slat.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);

        curtain.add(slat);
    }
    top_last_slats.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);
    curtain.remove(top_slat);

    //下部スラットを複製して設置
    let i_bottom = 0;
    for(i_bottom; i_bottom<repeat_bottom_num; i_bottom++) {
        const slat = bottom_slat.clone();
        slat.position.y = - (bottom_slat_height / 2) + center_mekas_position - (top_slat_height * i_bottom);
        slat.material = createMaterialWithCanvas(bottom_texture_arr[i_bottom % bottom_texture_repeat_num], bottom_opacity);

        curtain.add(slat);
    }
    curtain.remove(bottom_slat);


    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadPelrePairOpen(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!pelre_pair_model_open) {
        pelre_pair_model_open = await loadModel('model/24ペルレペア_フィーユ_ハンドル操作_ドレープ全開.gltf');
    }
    const curtain = pelre_pair_model_open.scenes[0].clone();
    console.log(curtain);

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const center_mekas = curtain.getObjectByName('meka_2');
    const bottom_mekas = curtain.getObjectByName('meka_3');
    // const top_slat = curtain.getObjectByName('slat');
    const bottom_slat = curtain.getObjectByName('slat_2');
    // const top_last_slats = curtain.getObjectByName('slat_3');
    const slat_tamari = curtain.getObjectByName('slat_tamari');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const center_mekas_height = getHeight(center_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const bottom_slat_height = getHeight(bottom_slat);
    const slat_tamari_height = getHeight(slat_tamari);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 35;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 50;
    //実際の高さを算出するための比率
    const real_scale = real_slat_height / bottom_slat_height;

    //下部スラットのリピート回数を算出
    const real_bottom_slats_height_sum = real_product_height - (top_mekas_height + slat_tamari_height + center_mekas_height + bottom_mekas_height) * real_scale;
    const repeat_bottom_num = Math.floor(real_bottom_slats_height_sum / real_slat_height);
    console.log('repeat_bottom_num', repeat_bottom_num);

    //-------------------------------ここからがメインの処理-------------------------------

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    center_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    bottom_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));

    //テクスチャの縦分割数
    const tamari_num = 2;
    const slat_height_ratio = 1 / (tamari_num + repeat_bottom_num); //全体に対する大きさの比率
    const top_texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    const bottom_texture_repeat_num = Math.floor(img_info.item_data.height_bottom / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const top_texture_arr = await createTextureArr(img_info, slat_height_ratio, top_texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    const bottom_texture_arr = await createTextureArr(img_info, slat_height_ratio, bottom_texture_repeat_num, img_info.item_data.name_bottom, img_info.item_data.width_bottom);
    //透明度の取得
    const top_opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;
    const bottom_opacity = isLace(img_info.item_data.ctgId_bottom, img_info.item_data.attrib_bottom) ? LACE_OPACITY : DRAPE_OPACITY;

    //スラット以外の位置設定
    //高さゼロ地点から各パーツを下に並べていって、最後に全体を中央に移動させる。
    const ex_height_2 = 0.008; //高さ調整
    top_mekas.position.y = - top_mekas_height / 2;
    slat_tamari.position.y = - top_mekas_height - slat_tamari_height / 2;
    const center_mekas_position = - top_mekas_height - slat_tamari_height;
    center_mekas.position.y = center_mekas_position - center_mekas_height / 2;

    const bottom_mekas_position = center_mekas_position - bottom_slat_height * repeat_bottom_num;
    bottom_mekas.position.y = bottom_mekas_position - center_mekas_height - bottom_mekas_height / 2 + ex_height_2;

    //上部スラットを複製して設置
    let i_top = 0;
    slat_tamari.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);

    //下部スラットを複製して設置
    let i_bottom = 0;
    for(i_bottom; i_bottom<repeat_bottom_num; i_bottom++) {
        const slat = bottom_slat.clone();
        slat.position.y = center_mekas_position - center_mekas_height - bottom_slat_height * i_bottom - bottom_slat_height / 2 + ex_height_2;
        slat.material = createMaterialWithCanvas(bottom_texture_arr[i_bottom % bottom_texture_repeat_num], bottom_opacity);
        curtain.add(slat);
    }
    curtain.remove(bottom_slat);

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadPelrePairClose(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!pelre_pair_model_close) {
        pelre_pair_model_close = await loadModel('model/24ペルレペア_フィーユ_ハンドル操作_ドレープ全閉.gltf');
    }
    const curtain = pelre_pair_model_close.scenes[0].clone();
    console.log(curtain);

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const center_mekas = curtain.getObjectByName('meka_2');
    const bottom_mekas = curtain.getObjectByName('meka_3');
    const top_slat = curtain.getObjectByName('slat');
    // const bottom_slat = curtain.getObjectByName('slat_2');
    // const top_last_slats = curtain.getObjectByName('slat_3');
    const slat_tamari = curtain.getObjectByName('slat_tamari');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const center_mekas_height = getHeight(center_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const top_slat_height = getHeight(top_slat);
    const slat_tamari_height = getHeight(slat_tamari);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 35;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 50;
    //実際の高さを算出するための比率
    const real_scale = real_slat_height / top_slat_height;

    //上部スラットのリピート回数を算出
    const real_top_slats_height_sum = real_product_height - (top_mekas_height + slat_tamari_height + center_mekas_height + bottom_mekas_height) * real_scale;
    const repeat_top_num = Math.floor(real_top_slats_height_sum / real_slat_height);
    console.log('repeat_top_num', repeat_top_num);

    //-------------------------------ここからがメインの処理-------------------------------

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    center_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    bottom_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));

    //テクスチャの縦分割数
    const slat_height_ratio = 1 / repeat_top_num; //全体に対する大きさの比率
    const top_texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const top_texture_arr = await createTextureArr(img_info, slat_height_ratio, top_texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    //透明度の取得
    const top_opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;

    //スラット以外の位置設定
    //高さゼロ地点から各パーツを下に並べていって、最後に全体を中央に移動させる。
    const ex_height_2 = 0.008; //高さ調整
    top_mekas.position.y = - top_mekas_height / 2;
    const center_mekas_position = - top_mekas_height - top_slat_height * repeat_top_num + ex_height_2;
    center_mekas.position.y = center_mekas_position - center_mekas_height / 2;
    slat_tamari.position.y = center_mekas_position - center_mekas_height - slat_tamari_height / 2;
    bottom_mekas.position.y = center_mekas_position - center_mekas_height - slat_tamari_height - bottom_mekas_height / 2;

    //上部スラットを複製して設置
    let i_top = 0;
    for(i_top; i_top<repeat_top_num; i_top++) {
        const slat = top_slat.clone();
        slat.position.y = - top_mekas_height - top_slat_height * i_top - top_slat_height / 2 + ex_height_2;
        slat.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);
        curtain.add(slat);
    }
    curtain.remove(top_slat);

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadPelrePairHalf(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!pelre_pair_model_half) {
        pelre_pair_model_half = await loadModel('model/20_ペルレペア.gltf');
    }
    const curtain = pelre_pair_model_half.scenes[0].clone();
    console.log(curtain);


    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const center_mekas = curtain.getObjectByName('meka_2');
    const bottom_mekas = curtain.getObjectByName('meka_3');
    const top_slat = curtain.getObjectByName('slat');
    const bottom_slat = curtain.getObjectByName('slat_2');
    const top_last_slats = curtain.getObjectByName('slat_3');
    const bottom_last_slats = curtain.getObjectByName('slat_4');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const center_mekas_height = getHeight(center_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const top_slat_height = getHeight(top_slat);
    const bottom_slat_height = getHeight(bottom_slat);
    const top_last_slats_height = getHeight(top_last_slats);
    const bottom_last_slats_height = getHeight(bottom_last_slats);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    const real_product_height_top = real_product_height / 10 * 6;
    const real_product_height_bottom = real_product_height / 10 * 4;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 35;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 50;

    //上部スラットのリピート回数を算出
    const real_scale = real_slat_height / top_slat_height;
    const real_top_mekas_height = top_mekas_height * real_scale;
    const real_center_mekas_height = center_mekas_height * real_scale;
    const real_top_last_slats_height = top_last_slats_height * real_scale;
    const repeat_top_num = Math.floor((real_product_height_top - real_top_mekas_height - real_center_mekas_height - real_top_last_slats_height) / real_slat_height);
    console.log('repeat_top_num', repeat_top_num);
    //下部スラットのリピート回数を算出
    const real_bottom_mekas_height = bottom_mekas_height * real_scale;
    const real_bottom_last_slats_height = bottom_last_slats_height * real_scale;
    const repeat_bottom_num = Math.floor((real_product_height_bottom - real_bottom_mekas_height - real_bottom_last_slats_height) / real_slat_height);
    console.log('repeat_bottom_num', repeat_bottom_num);

    // スラット以外の位置設定
    const ex_height = -0.012; //無理やり高さ調整した
    const ex_height_2 = -0.001;
    const ex_height_3 = -0.026;
    const ex_height_4 = -0.012;
    top_mekas.position.y = - top_mekas_height / 2;
    top_last_slats.position.y = - top_last_slats_height / 2 - top_mekas_height - (top_slat_height * repeat_top_num) - ex_height;
    const center_mekas_position = - top_mekas_height - (top_slat_height * repeat_top_num) - top_last_slats_height - ex_height_2;
    center_mekas.position.y = - center_mekas_height / 2 + center_mekas_position;

    const bottom_mekas_position = center_mekas_position - (bottom_slat_height * repeat_bottom_num) - bottom_last_slats_height + ex_height_3;
    bottom_last_slats.position.y = center_mekas_position - bottom_last_slats_height / 2 - (bottom_slat_height * repeat_bottom_num);
    bottom_mekas.position.y = bottom_mekas_height / 2 + bottom_mekas_position;

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    bottom_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    center_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));


    //テクスチャの縦分割数
    const slat_height_ratio = 1 / (repeat_top_num + repeat_bottom_num); //全体に対する大きさの比率
    const top_texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    const bottom_texture_repeat_num = Math.floor(img_info.item_data.height_bottom / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const top_texture_arr = await createTextureArr(img_info, slat_height_ratio, top_texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    const bottom_texture_arr = await createTextureArr(img_info, slat_height_ratio, bottom_texture_repeat_num, img_info.item_data.name_bottom, img_info.item_data.width_bottom);
    //透明度の取得
    const top_opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;
    const bottom_opacity = isLace(img_info.item_data.ctgId_bottom, img_info.item_data.attrib_bottom) ? LACE_OPACITY : DRAPE_OPACITY;

    //上部スラットを複製して設置
    let i_top = 0;
    for(i_top; i_top<repeat_top_num; i_top++) {
        const slat = top_slat.clone();
        slat.position.y = - (top_slat_height / 2) - top_mekas_height - (top_slat_height * i_top);
        slat.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);

        curtain.add(slat);
    }
    top_last_slats.material = createMaterialWithCanvas(top_texture_arr[i_top % top_texture_repeat_num], top_opacity);
    curtain.remove(top_slat);

    //下部スラットを複製して設置
    let i_bottom = 0;
    for(i_bottom; i_bottom<repeat_bottom_num; i_bottom++) {
        const slat = bottom_slat.clone();
        slat.position.y = - (bottom_slat_height / 2) + center_mekas_position - (top_slat_height * i_bottom) + ex_height_4;
        slat.material = createMaterialWithCanvas(bottom_texture_arr[i_bottom % bottom_texture_repeat_num], bottom_opacity);

        curtain.add(slat);
    }
    bottom_last_slats.material = createMaterialWithCanvas(bottom_texture_arr[i_bottom % bottom_texture_repeat_num], bottom_opacity);
    curtain.remove(bottom_slat);


    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadPelre(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!pelre_model) {
        pelre_model = await loadModel('model/21_ペルレ.gltf');
    }
    const curtain = pelre_model.scenes[0].clone();
    console.log(curtain);

    //設定値
    const slat_gap = 0;
    const top_meka_posi_condition = 0.01;
    const bottom_meka_posi_condition = 0.03;

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka');
    const bottom_mekas = curtain.getObjectByName('meka_2');
    const org_slat = curtain.getObjectByName('slat');
    const last_slats = curtain.getObjectByName('slat_2');

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const last_slats_height = getHeight(last_slats);
    const slat_height = getHeight(org_slat);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 35;
    //一山の生地の長さ（縦）mm
    const real_one_cloth_height = 50;

    //スラットのリピート回数を算出
    const real_top_mekas_height = top_mekas_height * (real_slat_height / slat_height);
    const real_bottom_mekas_height = bottom_mekas_height * (real_slat_height / slat_height);
    const real_last_slats_height = last_slats_height * (real_slat_height / slat_height);
    const repeat_num = Math.floor((real_product_height - real_top_mekas_height - real_bottom_mekas_height - real_last_slats_height) / real_slat_height);
    console.log('repeat_num', repeat_num);

    //上部と下部の位置設定
    const slat_height_sum = (repeat_num * slat_height + slat_gap * (repeat_num - 1));
    const top_mekas_y_position = slat_height_sum / 2 + top_mekas_height - top_meka_posi_condition;
    const last_slats_y_position = -slat_height_sum / 2 + bottom_meka_posi_condition;
    const bottom_mekas_y_position = -slat_height_sum / 2 + bottom_meka_posi_condition - last_slats_height;
    top_mekas.position.y = top_mekas_y_position;
    last_slats.position.y = last_slats_y_position;
    bottom_mekas.position.y = bottom_mekas_y_position;

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    bottom_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));


    //テクスチャの縦分割数
    const slat_height_ratio = 1 / (repeat_num); //全体に対する大きさの比率
    const texture_repeat_num = Math.floor(img_info.item_data.height / real_one_cloth_height);
    //分割したテクスチャを生成して配列に
    const texture_arr = await createTextureArr(img_info, slat_height_ratio, texture_repeat_num, img_info.item_data.name, img_info.item_data.width);
    //透明度の取得
    const opacity = isLace(img_info.item_data.ctgId, img_info.item_data.attrib) ? LACE_OPACITY : DRAPE_OPACITY;

    //スラットを複製して設置
    let i_top = 0;
    for(i_top; i_top<repeat_num; i_top++) {
        const slat = org_slat.clone();
        slat.position.y = slat_height_sum / 2 - (slat_height + slat_gap) * i_top;
        slat.material = createMaterialWithCanvas(texture_arr[i_top % texture_repeat_num], opacity);

        curtain.add(slat);
    }
    last_slats.material = createMaterialWithCanvas(texture_arr[i_top % texture_repeat_num], opacity);
    curtain.remove(org_slat);


    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadLineDrape(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!line_drape_model) {
        line_drape_model = await loadModel('model/7_ラインドレープ_バトン操作_スラット_角度垂直.gltf');
    }
    const curtain = line_drape_model.scenes[0].clone();
    // const curtain = line_drape_model.clone();
    console.log(curtain);


    //ユーザーが入力した製品幅mm
    const real_product_width = img_info.rect_width;
    //製品ごとに決まってるスラット幅mm
    const real_slat_width = img_info.slatWidth;
    //スラットのリピート回数を算出
    const repeat_num = Math.floor(real_product_width / real_slat_width);

    const slat_group = curtain.getObjectByName('slat');
    const line_group = curtain.getObjectByName('meka');
    const slat_name = 'cloth119';
    const org_cloth = slat_group.getObjectByName(slat_name);

    //レールの幅を取得
    const bbox1 = new THREE.Box3().setFromObject(line_group);
    const line_width = bbox1.max.x - bbox1.min.x;
    console.log('line_width', line_width);

    //スラットの幅を取得
    //計算用にいったんスラットを真横向かせる
    const org_cloth_rotation_y = org_cloth.rotation.y;
    org_cloth.rotation.set(0, 0, 0);
    const bbox2 = new THREE.Box3().setFromObject(org_cloth);
    const org_cloth_width = bbox2.max.x - bbox2.min.x;
    console.log('org_cloth_width', org_cloth_width);
    //元の向きに戻す
    org_cloth.rotation.set(0, org_cloth_rotation_y, 0);

    //スラットの高さもついでに取得
    const org_cloth_height = bbox2.max.y - bbox2.min.y;
    console.log('org_cloth_height', org_cloth_height);

    const cloth_obj_gap = 0.002; //逆にもっと隙間を空ける
    // const cloth_obj_gap = -0.001; //スラット間に隙間があると黒い線が出るので…

    //レールの拡大率を算出
    const ex_width = 0;
    const scaled_line_width = repeat_num * (org_cloth_width + cloth_obj_gap) + ex_width;
    const scale = scaled_line_width / line_width;
    // console.log('scale', scale);
    console.log('scaled_line_width', scaled_line_width);

    //全体のwidthを取得しておく
    //実際のキャンバス上でのスラットの大きさを計算するために後で使う
    const bbox0 = new THREE.Box3().setFromObject(curtain);
    const curtain_width = bbox0.max.x - bbox0.min.x;
    console.log('curtain_width', curtain_width);

    //レールの幅を設定（拡大縮小）
    //元のグループが向いてる方向によってx軸とy軸変わるので注意
    line_group.scale.x = scale;
    //レールの左端座標を取得
    const bbox3 = new THREE.Box3().setFromObject(line_group);
    const adjustment_num = 0.05;
    const line_left_x = bbox3.min.x + adjustment_num;
    console.log('line_left_x', line_left_x);


    //テクスチャ生成
    const prefix = img_info.prefix ? `${img_info.prefix}/` : '';
    const slat_texture_url = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name}.jpg`;
    const slat_texture = await loadTexture(slat_texture_url);

    let slat_texture_2;
    let slat_texture_3;
    let slat_texture_4;
    let slat_texture_5;
    let slat_texture_6;
    let slat_texture_7;
    let slat_texture_8;
    let slat_texture_9;
    let slat_texture_10;

    // 10本セットの製品（セイラス）
    // 1～9枚目はグラデーション、10枚目以降は無地
    const is_ten_set_slat = img_info.item_data.name === 'LD-7001' || img_info.item_data.name === 'LD-7002' || img_info.item_data.name === 'LD-7003' || img_info.item_data.name === 'LD-7004';
    const is_left_to_right = img_info.item_data.name === 'LD-7001' || img_info.item_data.name === 'LD-7003';
    if(is_ten_set_slat) {
        const slat_texture_url_2 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name2}.jpg`;
        const slat_texture_url_3 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name3}.jpg`;
        const slat_texture_url_4 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name4}.jpg`;
        const slat_texture_url_5 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name5}.jpg`;
        const slat_texture_url_6 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name6}.jpg`;
        const slat_texture_url_7 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name7}.jpg`;
        const slat_texture_url_8 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name8}.jpg`;
        const slat_texture_url_9 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name9}.jpg`;
        const slat_texture_url_10 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name10}.jpg`;
        slat_texture_2 = await loadTexture(slat_texture_url_2);
        slat_texture_3 = await loadTexture(slat_texture_url_3);
        slat_texture_4 = await loadTexture(slat_texture_url_4);
        slat_texture_5 = await loadTexture(slat_texture_url_5);
        slat_texture_6 = await loadTexture(slat_texture_url_6);
        slat_texture_7 = await loadTexture(slat_texture_url_7);
        slat_texture_8 = await loadTexture(slat_texture_url_8);
        slat_texture_9 = await loadTexture(slat_texture_url_9);
        slat_texture_10 = await loadTexture(slat_texture_url_10);        
    }

    //3本セットのテクスチャ
    const is_three_set_slat = !is_ten_set_slat && (typeof img_info.item_data.name2 !== 'undefined' && typeof img_info.item_data.name3 !== 'undefined');
    if(is_three_set_slat) {
        const slat_texture_url_2 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name2}.jpg`;
        const slat_texture_url_3 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name3}.jpg`;
        slat_texture_2 = await loadTexture(slat_texture_url_2);
        slat_texture_3 = await loadTexture(slat_texture_url_3);
    }

    const opacity = img_info.item_data.ctgId === 'lace' ? LACE_OPACITY : DRAPE_OPACITY;
    const is_closed_slat = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';

    //スラットの追加
    for(let i=0; i<repeat_num; i++) {
        const slats = slat_group.clone();

        //配置
        const x_position = line_left_x + org_cloth_width * i + cloth_obj_gap * i;
        slats.position.set(x_position, 0, 0);

        if(is_closed_slat) {
            //全閉（90度回転）
            slats.rotation.y = 1.45 + getSlatRotation(0.33, i, repeat_num);
        } else {
            //全開
            slats.rotation.y = getSlatRotation(0.15, i, repeat_num);
        }

        //スラットのテクスチャを設定
        const target_slat = slats.getObjectByName(slat_name);
        if(is_ten_set_slat) {
            if(is_left_to_right) {
                // 1～9はグラデーション、10以降は無地
                if((i+1)===1) target_slat.material = createMaterial(slat_texture, opacity);
                if((i+1)===2) target_slat.material = createMaterial(slat_texture_2, opacity);
                if((i+1)===3) target_slat.material = createMaterial(slat_texture_3, opacity);
                if((i+1)===4) target_slat.material = createMaterial(slat_texture_4, opacity);
                if((i+1)===5) target_slat.material = createMaterial(slat_texture_5, opacity);
                if((i+1)===6) target_slat.material = createMaterial(slat_texture_6, opacity);
                if((i+1)===7) target_slat.material = createMaterial(slat_texture_7, opacity);            
                if((i+1)===8) target_slat.material = createMaterial(slat_texture_8, opacity);
                if((i+1)===9) target_slat.material = createMaterial(slat_texture_9, opacity);
                if((i+1)>=10) target_slat.material = createMaterial(slat_texture_10, opacity);
            } else {
                if((repeat_num - i)===1) target_slat.material = createMaterial(slat_texture_10, opacity);
                if((repeat_num - i)===2) target_slat.material = createMaterial(slat_texture_9, opacity);
                if((repeat_num - i)===3) target_slat.material = createMaterial(slat_texture_8, opacity);
                if((repeat_num - i)===4) target_slat.material = createMaterial(slat_texture_7, opacity);
                if((repeat_num - i)===5) target_slat.material = createMaterial(slat_texture_6, opacity);
                if((repeat_num - i)===6) target_slat.material = createMaterial(slat_texture_5, opacity);
                if((repeat_num - i)===7) target_slat.material = createMaterial(slat_texture_4, opacity);
                if((repeat_num - i)===8) target_slat.material = createMaterial(slat_texture_3, opacity);
                if((repeat_num - i)===9) target_slat.material = createMaterial(slat_texture_2, opacity);
                if((repeat_num - i)>=10) target_slat.material = createMaterial(slat_texture, opacity);
            }
        } else if(is_three_set_slat) {
            // 3枚ごとに繰り返す
            const surplus = (i+1) % 3;
            if(surplus === 1) target_slat.material = createMaterial(slat_texture, opacity);
            if(surplus === 2) target_slat.material = createMaterial(slat_texture_2, opacity);
            if(surplus === 0) target_slat.material = createMaterial(slat_texture_3, opacity);
        } else {
            target_slat.material = createMaterial(slat_texture, opacity);
        }

        curtain.add(slats);
    }

    //オリジナルのスラットを削除
    curtain.remove(slat_group);

    //全閉のときは、スラット全体と同じサイズの板ポリを背景に置く（スラット間の区切りを綺麗に見せるため)
    if(is_closed_slat && opacity === DRAPE_OPACITY) {
        curtain.add(await getPlaneWallForLineDrape(curtain, slat_texture_url));
    }

    //部品色のテクスチャ生成
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    //部品色のテクスチャを設定
    curtain.getObjectByName("Line060").material = createMaterial(parts_texture);

    //座標を真ん中に修正
    const curtain_container = setCurtainCenter(curtain);
    const curtain_container2 = setCurtainCenter(curtain_container);

    console.groupEnd();
    return curtain_container2;
}

async function loadLineDrapePair(img_info) {
    console.groupCollapsed('loadCurtain');

    const is_closed_slat = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';
    const model_url = is_closed_slat ? 'model/10_ラインドレープペアアンサンブル_バトン操作_スラット角度全閉.gltf' : 'model/9_ラインドレープペアアンサンブル_バトン操作_スラット角度垂直.gltf';

    let curtain;
    if(is_closed_slat) {
        if (!line_drape_pair_model_close) {
            line_drape_pair_model_close = await loadModel(model_url);
        }
        curtain = line_drape_pair_model_close.scenes[0].clone();
    } else {
        if (!line_drape_pair_model_open) {
            line_drape_pair_model_open = await loadModel(model_url)
        }
        curtain = line_drape_pair_model_open.scenes[0].clone();
    }
    console.log(curtain);


    //スラット系をグループにまとめる
    const group_obj = new THREE.Object3D();
    group_obj.name = 'slat_group';
    group_obj.add(curtain.getObjectByName('slat'));
    group_obj.add(curtain.getObjectByName('slat_lace'));
    curtain.add(group_obj);

    //ユーザーが入力した製品幅mm
    const real_product_width = img_info.rect_width;
    //製品ごとに決まってるスラット幅mm
    const real_slat_width = img_info.slatWidth;
    //スラットのリピート回数を算出
    const repeat_num = Math.floor(real_product_width / real_slat_width);

    const slat_group = curtain.getObjectByName('slat_group');
    const line_group = curtain.getObjectByName('meka');
    const adjust_slat_to_rail = is_closed_slat ? 0.078 : 0.042 //スラット一本とレールがぴったり合う数値
    const rotation_y_to_face_front = 0; //いったん背景は無しで考える…。真正面を向くY軸の数値（スラット間の隙間とも合わせる）
    // const rotation_y_to_face_front = 0.19; //真正面を向くY軸の数値（スラット間の隙間とも合わせる）

    let ex_width_to_adjust_rail;
    let org_cloth_width;
    let cloth_obj_gap;
    if(is_closed_slat) {
        org_cloth_width = 0.08364844104756754; //開いたときの長さと同じ数値にしておく…
        cloth_obj_gap = 0;
        ex_width_to_adjust_rail = 0.05;
        console.log('org_cloth_width', org_cloth_width);
    } else {
        cloth_obj_gap = 0;
        ex_width_to_adjust_rail = 0;
        org_cloth_width = getWidth(slat_group);
        console.log('org_cloth_width', org_cloth_width);
    }

    //レールの幅を取得
    const line_width = getWidth(line_group);
    console.log('line_width', line_width);

    //スラットの高さもついでに取得
    const org_cloth_height = getHeight(slat_group);
    console.log('org_cloth_height', org_cloth_height);

    //レールの拡大率を算出
    const scaled_line_width = repeat_num * (org_cloth_width + cloth_obj_gap) + ex_width_to_adjust_rail;
    const scale = scaled_line_width / line_width;
    // console.log('scale', scale);
    console.log('scaled_line_width', scaled_line_width);

    //レールの幅を設定（拡大縮小）
    //元のグループが向いてる方向によってx軸とy軸変わるので注意
    line_group.scale.x = scale;
    //レールの左端座標を取得
    const bbox3 = new THREE.Box3().setFromObject(line_group);
    const line_left_x = bbox3.min.x + adjust_slat_to_rail;
    console.log('line_left_x', line_left_x);


    //テクスチャ生成（ドレープ）
    const prefix = img_info.prefix ? `${img_info.prefix}/` : '';
    const slat_texture_url = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name}.jpg`;
    const slat_texture = await loadTexture(slat_texture_url);

    let slat_texture_2;
    let slat_texture_3;
    let slat_texture_4;
    let slat_texture_5;
    let slat_texture_6;
    let slat_texture_7;
    let slat_texture_8;
    let slat_texture_9;
    let slat_texture_10;

    // 10本セットの製品（セイラス）
    // 1～9枚目はグラデーション、10枚目以降は無地
    const is_ten_set_slat = img_info.item_data.name === 'LD-7001' || img_info.item_data.name === 'LD-7002' || img_info.item_data.name === 'LD-7003' || img_info.item_data.name === 'LD-7004';
    const is_left_to_right = img_info.item_data.name === 'LD-7001' || img_info.item_data.name === 'LD-7003';
    if(is_ten_set_slat) {
        const slat_texture_url_2 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name2}.jpg`;
        const slat_texture_url_3 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name3}.jpg`;
        const slat_texture_url_4 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name4}.jpg`;
        const slat_texture_url_5 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name5}.jpg`;
        const slat_texture_url_6 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name6}.jpg`;
        const slat_texture_url_7 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name7}.jpg`;
        const slat_texture_url_8 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name8}.jpg`;
        const slat_texture_url_9 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name9}.jpg`;
        const slat_texture_url_10 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name10}.jpg`;
        slat_texture_2 = await loadTexture(slat_texture_url_2);
        slat_texture_3 = await loadTexture(slat_texture_url_3);
        slat_texture_4 = await loadTexture(slat_texture_url_4);
        slat_texture_5 = await loadTexture(slat_texture_url_5);
        slat_texture_6 = await loadTexture(slat_texture_url_6);
        slat_texture_7 = await loadTexture(slat_texture_url_7);
        slat_texture_8 = await loadTexture(slat_texture_url_8);
        slat_texture_9 = await loadTexture(slat_texture_url_9);
        slat_texture_10 = await loadTexture(slat_texture_url_10);        
    }
    
    //3本セットのテクスチャ
    const is_three_set_slat = !is_ten_set_slat && (typeof img_info.item_data.name2 !== 'undefined' && typeof img_info.item_data.name3 !== 'undefined');
    if(is_three_set_slat) {
        const slat_texture_url_2 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name2}.jpg`;
        const slat_texture_url_3 = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name3}.jpg`;
        slat_texture_2 = await loadTexture(slat_texture_url_2);
        slat_texture_3 = await loadTexture(slat_texture_url_3);
    }

    //テクスチャ生成（レース）
    const slat_texture_url_lace = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name_lace}.jpg`;
    const slat_texture_lace = await loadTexture(slat_texture_url_lace);
    const opacity = is_closed_slat ? DRAPE_OPACITY : LACE_OPACITY;

    //部品色のテクスチャ生成
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);

    //スラットの追加
    for(let i=0; i<repeat_num; i++) {
        const slats = slat_group.clone();

        //配置
        const x_position = line_left_x + org_cloth_width * i + cloth_obj_gap * i;
        slats.position.set(x_position, 0, 0);

        if(!is_closed_slat) {
            slats.rotation.y += getSlatRotation(0.13, i, repeat_num);
        }

        //スラットのテクスチャを設定
        const target_slat = slats.getObjectByName('kiji');
        if(is_ten_set_slat) {
            if(is_left_to_right) {
                // 1～9はグラデーション、10以降は無地
                if((i+1)===1) target_slat.material = createMaterial(slat_texture);
                if((i+1)===2) target_slat.material = createMaterial(slat_texture_2);
                if((i+1)===3) target_slat.material = createMaterial(slat_texture_3);
                if((i+1)===4) target_slat.material = createMaterial(slat_texture_4);
                if((i+1)===5) target_slat.material = createMaterial(slat_texture_5);
                if((i+1)===6) target_slat.material = createMaterial(slat_texture_6);
                if((i+1)===7) target_slat.material = createMaterial(slat_texture_7);            
                if((i+1)===8) target_slat.material = createMaterial(slat_texture_8);
                if((i+1)===9) target_slat.material = createMaterial(slat_texture_9);
                if((i+1)>=10) target_slat.material = createMaterial(slat_texture_10);
            } else {
                if((repeat_num - i)===1) target_slat.material = createMaterial(slat_texture_10);
                if((repeat_num - i)===2) target_slat.material = createMaterial(slat_texture_9);
                if((repeat_num - i)===3) target_slat.material = createMaterial(slat_texture_8);
                if((repeat_num - i)===4) target_slat.material = createMaterial(slat_texture_7);
                if((repeat_num - i)===5) target_slat.material = createMaterial(slat_texture_6);
                if((repeat_num - i)===6) target_slat.material = createMaterial(slat_texture_5);
                if((repeat_num - i)===7) target_slat.material = createMaterial(slat_texture_4);
                if((repeat_num - i)===8) target_slat.material = createMaterial(slat_texture_3);
                if((repeat_num - i)===9) target_slat.material = createMaterial(slat_texture_2);
                if((repeat_num - i)>=10) target_slat.material = createMaterial(slat_texture);
            }
        } else if(is_three_set_slat) {
            // 3枚ごとに繰り返す
            const surplus = (i+1) % 3;
            if(surplus === 1) target_slat.material = createMaterial(slat_texture);
            if(surplus === 2) target_slat.material = createMaterial(slat_texture_2);
            if(surplus === 0) target_slat.material = createMaterial(slat_texture_3);
        } else {
            target_slat.material = createMaterial(slat_texture);
        }

        //レースのテクスチャを設定
        slats.getObjectByName('kiji_lace').material = createMaterial(slat_texture_lace, opacity);

        //メカ部分にもテクスチャを反映する
        slats.getObjectByName('meka_2').material = createMaterial(parts_texture);
        slats.getObjectByName('meka_2_lace').material = createMaterial(parts_texture);

        curtain.add(slats);
    }

    //mekaに部品色のテクスチャを設定
    line_group.children.forEach(mesh => {
        mesh.material = createMaterial(parts_texture);
    });

    //オリジナルのスラットを削除
    curtain.remove(slat_group);

    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadLineDrapeWood(img_info) {
    console.groupCollapsed('loadCurtain');

    if (!line_drape_wood_model) {
        line_drape_wood_model = await loadModel('model/11_ラインドレープ木製_ツーコード操作_スラット角度垂直.gltf');
    }
    const curtain = line_drape_wood_model.scenes[0].clone();
    console.log(curtain);


    //ユーザーが入力した製品幅mm
    const real_product_width = img_info.rect_width;
    //製品ごとに決まってるスラット幅mm
    const real_slat_width = img_info.slatWidth;
    //スラットのリピート回数を算出
    const repeat_num = Math.floor(real_product_width / real_slat_width);

    const slat_obj_name = 'slat_mesh';
    const slat_group = curtain.getObjectByName('slat');
    const line_group = curtain.getObjectByName('meka');
    const adjust_slat_to_line = 0.044; //大きくするとレールが左に動く


    //レールの幅を取得
    const bbox1 = new THREE.Box3().setFromObject(line_group);
    const line_width = bbox1.max.x - bbox1.min.x;
    console.log('line_width', line_width);

    //スラットの幅を取得
    const org_cloth = curtain.getObjectByName(slat_obj_name);
    //計算用にいったんスラットを真横向かせる
    const org_cloth_rotation_z = org_cloth.rotation.z;
    org_cloth.rotation.z = 0;
    const bbox2 = new THREE.Box3().setFromObject(org_cloth);
    const org_cloth_width = bbox2.max.x - bbox2.min.x;
    console.log('org_cloth_width', org_cloth_width);
    //元の向きに戻す
    org_cloth.rotation.z = org_cloth_rotation_z;

    //スラットの高さもついでに取得
    const org_cloth_height = bbox2.max.y - bbox2.min.y;
    console.log('org_cloth_height', org_cloth_height);

    const cloth_obj_gap = -0.025;
    // const cloth_obj_gap = 0.0025; //逆にもっと隙間を空けてみる
    // const cloth_obj_gap = -0.001; //スラット間に隙間があると黒い線が出るので…

    //レールの拡大率を算出
    const ex_width = 0.025; //レールの幅が足りなかったので追加
    const scaled_line_width = repeat_num * (org_cloth_width + cloth_obj_gap) + ex_width;
    const scale = scaled_line_width / line_width;
    // console.log('scale', scale);
    console.log('scaled_line_width', scaled_line_width);

    //全体のwidthを取得しておく
    //実際のキャンバス上でのスラットの大きさを計算するために後で使う
    const bbox0 = new THREE.Box3().setFromObject(curtain);
    const curtain_width = bbox0.max.x - bbox0.min.x;
    console.log('curtain_width', curtain_width);

    //レールの幅を設定（拡大縮小）
    //元のグループが向いてる方向によってx軸とy軸変わるので注意
    line_group.scale.x = scale;
    //レールの左端座標を取得
    const bbox3 = new THREE.Box3().setFromObject(line_group);
    const line_left_x = bbox3.min.x + adjust_slat_to_line;
    console.log('line_left_x', line_left_x);


    //テクスチャ生成
    const prefix = img_info.prefix ? `${img_info.prefix}/` : '';
    const slat_texture_url = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name}.jpg`;
    const slat_texture = await loadTexture(slat_texture_url);

    const is_closed_slat = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';

    //スラットの追加
    for(let i=0; i<repeat_num; i++) {
        const slat = slat_group.clone();

        //配置
        const x_position = line_left_x + org_cloth_width * i + cloth_obj_gap * i;
        slat.position.set(x_position, 0, 0);

        //全閉のとき（90度回転）
        if(is_closed_slat) {
            slat.rotation.y = 1.55 + getSlatRotation(0.27, i, repeat_num);
        } else {
            //全開のとき
            slat.rotation.y += getSlatRotation(0.15, i, repeat_num);
        }

        //スラットのテクスチャを設定
        //既存マテリアルのmapにテクスチャをあてると色が暗くなるので…
        slat.getObjectByName(slat_obj_name).material = createMaterial(slat_texture);

        //「木製」では、メカ部分にもテクスチャを反映する
        slat.getObjectByName('meka_2').material = createMaterial(slat_texture);

        curtain.add(slat);
    }

    //オリジナルのスラットを削除
    curtain.remove(slat_group);

    //部品色のテクスチャ生成
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    //部品色のテクスチャを設定
    line_group.children.forEach(mesh => {
        mesh.material = createMaterial(parts_texture);
    });

    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadAR(img_info) {
    console.groupCollapsed('loadCurtain');

    const is_closed_slat = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';
    const model_url = is_closed_slat ? 'model/エアレ_全閉.gltf' : 'model/エアレ_全開.gltf';

    let curtain;
    if(is_closed_slat) {
        if (!ar_model_close) {
            ar_model_close = await loadModel(model_url);
        }
        curtain = ar_model_close.scenes[0].clone();
    } else {
        if (!ar_model_open) {
            ar_model_open = await loadModel(model_url)
        }
        curtain = ar_model_open.scenes[0].clone();
    }
    console.log(curtain);

    const clip_name_1 = is_closed_slat ? 'clip_1' : 'clip';
    const clip_name_2 = is_closed_slat ? 'clip_2' : 'clip_(1)';

    //スラット系をグループにまとめる
    const group_obj = new THREE.Object3D();
    group_obj.name = 'slat_group';
    group_obj.add(curtain.getObjectByName(clip_name_1));
    group_obj.add(curtain.getObjectByName(clip_name_2));
    group_obj.add(curtain.getObjectByName('slat'));
    group_obj.add(curtain.getObjectByName('lace'));
    curtain.add(group_obj);

    //ユーザーが入力した製品幅mm
    const real_product_width = img_info.rect_width;
    //製品ごとに決まってるスラット幅mm
    const real_slat_width = img_info.slatWidth;
    //スラットのリピート回数を算出
    const repeat_num = Math.floor(real_product_width / real_slat_width);

    const slat_group = curtain.getObjectByName('slat_group');
    const line_group = curtain.getObjectByName('meka');
    const adjust_slat_to_rail = is_closed_slat ? 0.078 : 0.078 //★★★スラット一本とレールがぴったり合う数値(閉じた状態を基準に)

    let ex_width_to_adjust_rail;
    let org_cloth_width;
    let cloth_obj_gap;
    if(is_closed_slat) {
        org_cloth_width = 0.10603732306495035; //★★★開いたときの長さと同じ数値にしてね！
        cloth_obj_gap = 0;
        ex_width_to_adjust_rail = 0.095; //★★★こっちで調整
        console.log('org_cloth_width', org_cloth_width);
    } else {
        cloth_obj_gap = 0;
        ex_width_to_adjust_rail = 0.095; //開いてるときもレールの長さが同じになるように

        //スラットの幅を取得
        org_cloth_width = getWidth(slat_group);
        console.log('org_cloth_width', org_cloth_width);
    }


    //レールの幅を取得
    const line_width = getWidth(line_group);
    console.log('line_width', line_width);

    //スラットの高さもついでに取得
    const org_cloth_height = getHeight(slat_group);
    console.log('org_cloth_height', org_cloth_height);

    //レールの拡大率を算出
    const scaled_line_width = repeat_num * (org_cloth_width + cloth_obj_gap) + ex_width_to_adjust_rail;
    const scale = scaled_line_width / line_width;
    // console.log('scale', scale);
    console.log('scaled_line_width', scaled_line_width);

    //レールの幅を設定（拡大縮小）
    //元のグループが向いてる方向によってx軸とy軸変わるので注意
    line_group.scale.x = scale;
    //レールの左端座標を取得
    const bbox3 = new THREE.Box3().setFromObject(line_group);
    const line_left_x = bbox3.min.x + adjust_slat_to_rail;
    console.log('line_left_x', line_left_x);


    //ドレープとレースのテクスチャ生成
    const prefix = img_info.prefix ? `${img_info.prefix}/` : '';
    const slat_texture_url = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name}.jpg`;
    const slat_texture = await loadTexture(slat_texture_url);
    const opacity = is_closed_slat ? LACE_OPACITY : 0.3;

    //部品色のテクスチャ生成
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);

    //スラットの追加
    for(let i=0; i<repeat_num; i++) {
        const slat = slat_group.clone();

        //配置
        const x_position = line_left_x + org_cloth_width * i + cloth_obj_gap * i;
        slat.position.set(x_position, 0, 0);

        //スラットのテクスチャを設定
        slat.getObjectByName('slat').children.forEach(mesh => {
            //ドレープ上部の芯地の色を少し明るくする
            if(is_closed_slat && mesh.name === '') {
                mesh.material = new THREE.MeshStandardMaterial({
                    map: slat_texture,
                    side: THREE.DoubleSide,
                    emissive: 0xffffff,
                    emissiveIntensity: 0.05,
                });
            //「採光」のとき芯地が目立ちすぎるのでレースに合わせて少し透過させる
            } else if(!is_closed_slat && mesh.name === 'drape_1') {
                mesh.material = new THREE.MeshStandardMaterial({
                    map: slat_texture,
                    side: THREE.DoubleSide,
                    emissive: 0xffffff,
                    emissiveIntensity: 0.075,
                    transparent: true,
                    opacity: 0.6,
                });
            //ドレープ
            } else {
                mesh.material = new THREE.MeshStandardMaterial({
                    map: slat_texture,
                    side: THREE.DoubleSide,
                });
            }
        });

        //レースのテクスチャを設定
        slat.getObjectByName('lace').material = createMaterial(slat_texture, opacity);

        //メカ部分にもテクスチャを反映する
        slat.getObjectByName(clip_name_1).material = createMaterial(parts_texture);
        slat.getObjectByName(clip_name_2).material = createMaterial(parts_texture);

        curtain.add(slat);
    }

    //mekaに部品色のテクスチャを設定
    line_group.children.forEach(mesh => {
        mesh.material = createMaterial(parts_texture);
    });

    //オリジナルのスラットを削除
    curtain.remove(slat_group);

    const curtain_container = setCurtainCenter(curtain);

    console.groupEnd();
    return curtain_container;
}

async function loadRollScreenLark(img_info) {
    console.groupCollapsed('loadCurtain');

    const is_exposed = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'exposed';
    const model_url = is_exposed ? 'model/13_ラルクシールド_プルコード操作_ウェイトバー露出.gltf' : 'model/14_ラルクシールド_プルコード操作_ウェイトバー袋縫.gltf';

    let curtain;
    if(is_exposed) {
        if (!roll_screen_lark_model_exposure) {
            roll_screen_lark_model_exposure = await loadModel(model_url);
        }
        curtain = roll_screen_lark_model_exposure.scenes[0].clone();
    } else {
        if (!roll_screen_lark_model_bag) {
            roll_screen_lark_model_bag = await loadModel(model_url);
        }
        curtain = roll_screen_lark_model_bag.scenes[0].clone();
    }
    console.log(curtain);

    //生地のテクスチャ
    //スダレ（ユキミ除く）、レース、ルセットは透過させる
    const is_yukimi = img_info.item_data.name === 'RS-1655' || img_info.item_data.name === 'RS-1656' || img_info.item_data.name === 'RS-1657';
    const is_transparent_sudare = img_info.item_data.ctgId === 'sudare-ish' && !is_yukimi;
    const is_lace = img_info.item_data.ctgId === 'lace';
    const is_recette = img_info.item_data.name === 'RS-1018' || img_info.item_data.name === 'RS-1019';
    const opacity = is_transparent_sudare || is_lace || is_recette ? LACE_OPACITY : DRAPE_OPACITY;

    //貼り付け対象のサイズに合わせるために、
    //createCanvasTextureの引数に、製品全体に対する対象の大きさの比率を渡す。
    const one_repeat = await createCanvasOneRepeat(img_info);
    const whole_width = getWidth(curtain);
    const whole_height = getHeight(curtain);

    //メイン生地
    const slat_texture = await createCanvasTexture(img_info, one_repeat);
    curtain.getObjectByName('slat').material = createMaterialWithCanvas(slat_texture, opacity);

    //ヘッダー部分の生地
    const slat_2_width = getWidth(curtain.getObjectByName('slat_2'));
    const slat_2_height = getHeight(curtain.getObjectByName('slat_2'));
    const slat_2_texture = await createCanvasTexture(img_info, one_repeat, slat_2_width/whole_width, slat_2_height/whole_height);
    curtain.getObjectByName('slat_2').material = createMaterialWithCanvas(slat_2_texture, opacity);
    //ヘッダー部分は生地なし（シールド露出）
    if(!img_info.shield) {
        curtain.getObjectByName('slat_2').visible = false;
    }

    //ウェイトバー部分の生地
    if(!is_exposed) {
        const slat_3_width = getWidth(curtain.getObjectByName('slat_3'));
        const slat_3_height = getHeight(curtain.getObjectByName('slat_3'));
        const slat_3_texture = await createCanvasTexture(img_info, one_repeat, slat_3_width/whole_width, slat_3_height*2/whole_height);
        curtain.getObjectByName('slat_3').material = createMaterialWithCanvas(slat_3_texture, opacity);
    }

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    curtain.getObjectByName('meka').children.forEach((mesh, index) => {
        if(index === 0) return; //[0]にslat_2が入ってるため
        mesh.material = createMaterial(parts_texture);
    });
    //ウェイトバー
    const weight_bar_texture = typeof img_info.weight_bar === 'undefined' ? parts_texture : await loadTexture(`item_image/parts/${img_info.weight_bar}.jpg`);
    curtain.getObjectByName('meka_2').children.forEach((mesh, index) => {
        if(!is_exposed && index === 0) return; //袋縫いのときだけ[0]にslat_3が入ってるため
        mesh.material = createMaterial(weight_bar_texture);
    });

    console.groupEnd();
    return curtain;
}

async function loadRollScreenLarkLarge(img_info) {
    console.groupCollapsed('loadCurtain');

    const model_url = 'model/ラルク大型.gltf';

    if (!roll_screen_lark_large_model) {
        roll_screen_lark_large_model = await loadModel(model_url);
    }
    const curtain = roll_screen_lark_large_model.scenes[0].clone();

    console.log(curtain);

    //生地のテクスチャ
    //スダレ、レースは透過させる
    const opacity = img_info.item_data.ctgId === 'sudare-ish' || img_info.item_data.ctgId === 'lace' ? LACE_OPACITY : DRAPE_OPACITY;

    //貼り付け対象のサイズに合わせるために、
    //createCanvasTextureの引数に、製品全体に対する対象の大きさの比率を渡す。
    const one_repeat = await createCanvasOneRepeat(img_info);
    const whole_width = getWidth(curtain);
    const whole_height = getHeight(curtain);

    //生地メイン
    const slat_1_width = getWidth(curtain.getObjectByName('Kiji_1'));
    const slat_1_height = getHeight(curtain.getObjectByName('Kiji_1'));
    const slat_1_texture = await createCanvasTexture(img_info, one_repeat, slat_1_width/whole_width, slat_1_height/whole_height);
    curtain.getObjectByName('Kiji_1').material = createMaterialWithCanvas(slat_1_texture, opacity);

    //生地ヘッダー部分
    const slat_2_width = getWidth(curtain.getObjectByName('Donut001'));
    const slat_2_height = getHeight(curtain.getObjectByName('Donut001'));
    //ヘッダーの生地が引き伸ばされる問題の暫定対応（横に伸びるのがどうにもならない…）
    const slat_2_texture = await createCanvasTexture(img_info, one_repeat, slat_2_width/whole_width, slat_2_height/whole_height * 10);
    // const slat_2_texture = await createCanvasTexture(img_info, one_repeat, slat_2_width/whole_width, slat_2_height/whole_height * Math.PI);
    curtain.getObjectByName('Donut001').material = createMaterialWithCanvas(slat_2_texture);

    //ウェイトバー
    const weight_bar_texture = await loadTexture(`item_image/parts/${img_info.weight_bar}.jpg`);
    curtain.getObjectByName('Tube001').material = createMaterial(weight_bar_texture);

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    curtain.getObjectByName('Line004').material = createMaterial(parts_texture);
    curtain.getObjectByName('Line123460502').material = createMaterial(parts_texture);
    curtain.getObjectByName('AAAre_kanagu').material = createMaterial(parts_texture);
    curtain.getObjectByName('AAAre_kanagu001').material = createMaterial(parts_texture);

    console.groupEnd();
    return curtain;
}

async function loadRollScreenDuole(img_info) {
    console.groupCollapsed('loadCurtain');

    const is_closed = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';
    const model_url = 'model/15_16_デュオレ.gltf';

    if (!roll_screen_duole_model) {
        roll_screen_duole_model = await loadModel(model_url);
    }
    const curtain = roll_screen_duole_model.scenes[0].clone();

    console.log(curtain);

    //生地のテクスチャ
    //貼り付け対象のサイズに合わせるために、
    //createCanvasTextureの引数に、製品全体に対する対象の大きさの比率を渡す。
    const name_suffix = is_closed ? '' : '_o';
    const one_repeat = await createCanvasOneRepeat(img_info, name_suffix);
    const whole_width = getWidth(curtain);
    const whole_height = getHeight(curtain);

    const slat_texture = await createCanvasTexture(img_info, one_repeat);
    curtain.getObjectByName('slat').material = createMaterialWithCanvas(slat_texture);


    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    curtain.getObjectByName('meka').children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    curtain.getObjectByName('meka_2').children.forEach(mesh => mesh.material = createMaterial(parts_texture));

    //上部生地にもテクスチャ反映
    curtain.getObjectByName("Cylinder023").material = createMaterialWithCanvas(slat_texture);

    console.groupEnd();
    return curtain;
}

async function loadRollScreenDuoleSlim(img_info) {
    console.groupCollapsed('loadCurtain');

    const is_closed = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';
    const model_url = 'model/17_デュオレスリム.gltf';

    if (!roll_screen_duole_slim_model) {
        roll_screen_duole_slim_model = await loadModel(model_url);
    }
    const curtain = roll_screen_duole_slim_model.scenes[0].clone();

    console.log(curtain);

    //生地のテクスチャ
    //貼り付け対象のサイズに合わせるために、
    //createCanvasTextureの引数に、製品全体に対する対象の大きさの比率を渡す。
    const name_suffix = is_closed ? '' : '_o';
    const one_repeat = await createCanvasOneRepeat(img_info, name_suffix);

    const slat_texture = await createCanvasTexture(img_info, one_repeat);
    curtain.getObjectByName('slat').material = createMaterialWithCanvas(slat_texture);

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    curtain.getObjectByName('meka').children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    curtain.getObjectByName('meka_2').children.forEach(mesh => mesh.material = createMaterial(parts_texture));

    console.groupEnd();
    return curtain;
}

async function loadRollScreenLumie(img_info) {
    console.groupCollapsed('loadCurtain');

    //開閉
    const is_closed_slat = typeof img_info.feature.value === 'undefined' || img_info.feature.value === 'close';

    let curtain;
    if(is_closed_slat) {
        if (!lumie_model_close) {
            lumie_model_close = await loadModel('model/23_ルミエ_全閉.gltf');
        }
        curtain = lumie_model_close.scenes[0].clone();
    } else {
        if (!lumie_model_open) {
            lumie_model_open = await loadModel('model/23_ルミエ_全開.gltf')
        }
        curtain = lumie_model_open.scenes[0].clone();
    }
    console.log(curtain);

    //設定値
    const slat_gap = 0.001;

    //各3Dオブジェクト
    const top_mekas = curtain.getObjectByName('meka1');
    const bottom_mekas = curtain.getObjectByName('meka2');
    const org_slat = curtain.getObjectByName('kiji');

    //生地のテクスチャ
    const prefix = img_info.prefix ? `${img_info.prefix}/` : '';
    const slat_texture_url = `item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name}.jpg`;
    const slat_texture = await loadTexture(slat_texture_url);
    org_slat.material = createMaterial(slat_texture);

    //部品色のテクスチャ
    const parts_texture = await loadTexture(`item_image/parts/${img_info.parts}.jpg`);
    top_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));
    bottom_mekas.children.forEach(mesh => mesh.material = createMaterial(parts_texture));

    //それぞれの高さ
    const top_mekas_height = getHeight(top_mekas);
    const bottom_mekas_height = getHeight(bottom_mekas);
    const slat_height = getHeight(org_slat);

    //ユーザーが入力した製品高mm
    const real_product_height = img_info.rect_height;
    //製品ごとに決まってるスラット高mm
    const real_slat_height = 65;
    //スラットのリピート回数を算出
    const real_top_mekas_height = top_mekas_height * (real_slat_height / slat_height);
    const real_bottom_mekas_height = bottom_mekas_height * (real_slat_height / slat_height);
    const repeat_num = Math.floor((real_product_height - real_top_mekas_height - real_bottom_mekas_height) / real_slat_height);
    // console.log('repeat_num', repeat_num);

    //上部と下部の位置設定
    const fix_gap = 0.06; //0.005
    const slat_height_sum = (repeat_num * slat_height + slat_gap * (repeat_num - 1));
    const top_mekas_y_position = slat_height_sum / 2 + top_mekas_height - fix_gap;
    const bottom_mekas_y_position = -slat_height_sum / 2;
    top_mekas.position.y = top_mekas_y_position;
    bottom_mekas.position.y = bottom_mekas_y_position;

    //スラットを複製して設置
    for(let i=0; i<repeat_num; i++) {
        const slat = org_slat.clone();
        slat.position.y = slat_height_sum / 2 - (slat_height + slat_gap) * i;
        curtain.add(slat);
    }
    //元のスラットを消す
    curtain.remove(org_slat);

    //レースを前面に設置（ドレープと同じテクスチャを透過させて使う）
    curtain.add(makeFrontLaceMesh(curtain, slat_texture));

    //座標を上下左右で中央寄せ
    const curtain_container = setCurtainCenter2(curtain, top_mekas, bottom_mekas);

    console.groupEnd();
    return curtain_container;
}

function makeFrontLaceMesh(curtain, slat_texture) {
    const bbox_slat_first = new THREE.Box3().setFromObject(curtain.getObjectByName('kiji'));
    const bbox_slat_last = new THREE.Box3().setFromObject(curtain.children[curtain.children.length - 1]);

    const x_size = bbox_slat_first.max.x - bbox_slat_first.min.x + 0.01; //微調整
    const y_size = bbox_slat_first.max.y - bbox_slat_last.min.y + 0.05; //微調整
    const z_position = bbox_slat_first.max.z; //前面

    const geometry = new THREE.PlaneGeometry(x_size, y_size);
    const material = new THREE.MeshStandardMaterial({
        map: slat_texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.4,
    });
    const plane = new THREE.Mesh(geometry, material);
    plane.position.z = z_position;
    plane.position.x = -0.087; //微調整
    return plane;
}

async function loadAssumedModel() {
    console.groupCollapsed('loadCurtain');

    if (!line_drape_wood_model) {
        line_drape_wood_model = await loadModel('model/11_ラインドレープ木製_ツーコード操作_スラット角度垂直.gltf');
    }

    const curtain = line_drape_wood_model.scenes[0].clone();

    console.groupEnd();
    return curtain;
}

function createLight(x, y, z, intensity) {
    const color = 0xFFFFFF;
    const light = new THREE.DirectionalLight(color, intensity);
    light.position.set(x, y, z);
    light.target.position.set(0, 0, 0);

    return light;
}

function getWidth(obj) {
    const bbox = new THREE.Box3().setFromObject(obj);
    return bbox.max.x - bbox.min.x;
}
function getHeight(obj) {
    const bbox = new THREE.Box3().setFromObject(obj);
    return bbox.max.y - bbox.min.y;
}

async function getPlaneWall(curtain, slat_texture_url, meka_posi_condition = '') {
    const rgbs = await getRgbForPlane(slat_texture_url);
    const bbox_slat_first = new THREE.Box3().setFromObject(curtain.getObjectByName('slat'));
    const bbox_slat_last = new THREE.Box3().setFromObject(curtain.children[curtain.children.length - 1]);

    const x_size = bbox_slat_first.max.x - bbox_slat_first.min.x;
    const y_size = bbox_slat_first.max.y - bbox_slat_last.min.y;
    const z_position = bbox_slat_first.min.z;

    const geometry = new THREE.PlaneGeometry(x_size, y_size);
    const material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(`rgb(${rgbs[0]}, ${rgbs[1]}, ${rgbs[2]})`).getHex(),
    });
    const plane = new THREE.Mesh(geometry, material);
    plane.position.z = z_position;
    if(meka_posi_condition) plane.position.y = meka_posi_condition;

    return plane;
}
async function getPlaneWallForLineDrape(curtain, slat_texture_url) {
    const rgbs = await getRgbForPlane(slat_texture_url);
    const bbox_slat_first = new THREE.Box3().setFromObject(curtain.getObjectByName('slat'));
    const bbox_slat_last = new THREE.Box3().setFromObject(curtain.children[curtain.children.length - 1]);

    const to_justify_size = 0.05;
    const x_size = bbox_slat_last.max.x - (bbox_slat_first.min.x + to_justify_size);
    const y_size = bbox_slat_last.max.y - (bbox_slat_first.min.y + to_justify_size);
    const z_position = bbox_slat_first.min.z;

    const geometry = new THREE.PlaneGeometry(x_size, y_size);
    const material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(`rgb(${rgbs[0]}, ${rgbs[1]}, ${rgbs[2]})`).getHex(),
    });
    const plane = new THREE.Mesh(geometry, material);
    plane.position.z = z_position;

    return plane;
}

async function getRgbForPlane(texture_img_url) {
    const colorThief = new ColorThief();
    const img = await new Promise(r => {
        const img = new Image();
        img.addEventListener('load', () => r(img));
        img.crossOrigin = 'Anonymous';
        img.src = texture_img_url;
    });
    //主要色をRGBで取得
    let dominant_rgbs;
    try {
        console.log(colorThief.getColor(img));
        dominant_rgbs = colorThief.getColor(img);
    } catch {
        dominant_rgbs = [255, 255, 255];
    }
    const rgb_sum = dominant_rgbs.reduce((previous, current) => previous + current);
    // console.log(rgb_sum);

    //主要色が明るければそれより暗い色を設定、暗ければ明るい色を設定。
    const rgbs = [];
    const color_dif = 100;
    const is_light_color = rgb_sum > 150;
    rgbs[0] = is_light_color ? Math.max(dominant_rgbs[0] - color_dif, 0) : dominant_rgbs[0] + color_dif;
    rgbs[1] = is_light_color ? Math.max(dominant_rgbs[1] - color_dif, 0) : dominant_rgbs[1] + color_dif;
    rgbs[2] = is_light_color ? Math.max(dominant_rgbs[2] - color_dif, 0) : dominant_rgbs[2] + color_dif;
    console.log(rgbs);

    return rgbs;
}

function setCurtainCenter(curtain) {
    const bbox = new THREE.Box3().setFromObject(curtain);
    const center_condition_x = (bbox.max.x + bbox.min.x) / 2;
    curtain.position.x -= center_condition_x;

    const center_condition_y = (bbox.max.y + bbox.min.y) / 2;
    curtain.position.y -= center_condition_y;

    const curtain_container = new THREE.Object3D();
    curtain_container.add(curtain);

    return curtain_container;
}

function setCurtainCenter2(curtain, top_obj, bottom_obj) {
    //上下中央寄せ
    const bbox_top = new THREE.Box3().setFromObject(top_obj);
    const bbox_bottom = new THREE.Box3().setFromObject(bottom_obj);
    const center_condition = (bbox_top.max.y + bbox_bottom.min.y) / 2;
    curtain.position.y -= center_condition;
    //左右中央寄せ
    const bbox_curtain = new THREE.Box3().setFromObject(curtain);
    const center_condition2 = (bbox_top.max.x + bbox_bottom.min.x) / 2;
    curtain.position.x -= center_condition2;

    const curtain_container = new THREE.Object3D();
    curtain_container.add(curtain);
    return curtain_container;
}

function slideRight(obj, tape_gap) {
    return obj.position.x + tape_gap / 2;
}

function getSlatRotation(magic_number, i, repeat_num) {
    magic_number = magic_number * 0.02; //マイルームシミュレーション内ではx0.02でちょうど合う
    const m_degree = magic_number - (1 - repeat_num / 10) * magic_number; //repeat10で試算したので…
    return (1 - (i / (repeat_num / 2))) * m_degree;
}

function loadModel(model_url) {
    const loader = new GLTFLoader();
    return new Promise(r => {
        loader.load(
            base_path + model_url,
            obj => r(obj), '', err => console.log('モデルの読み込みに失敗')
        );
    });
}
function loadTexture(texture_url) {
    return new Promise((resolve, reject) => {
        new THREE.TextureLoader().load(
            texture_url,
            texture => {
                //テクスチャ上下反転（もらった3Dモデルだと逆に表示されるので…）
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, -1);
                resolve(texture)
            },
            '',
            err => reject('部品色のテクスチャ読み込みに失敗')
        );
    });
}

function createMaterial(texture, opacity = 1) {
    return new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: opacity,
    });
}
function createMaterialWithCanvas(texture, opacity = 1) {
    return new THREE.MeshStandardMaterial({
        map: new THREE.CanvasTexture(texture),
        side: THREE.DoubleSide,
        transparent: true,
        opacity: opacity,
    });
}

async function createCanvasOneRepeat(img_info, name_suffix = '') {
    const cv = document.createElement('canvas');

    try{
        const prefix = img_info.prefix ? `${img_info.prefix}/` : '';

        //デュオレとデュオレスリムが採光のときは、透過pngを読み込む。
        const img_suffix = name_suffix && (img_info.productType === 'DU' || img_info.productType === 'DU-SLIM') ? 'png' : 'jpg';

        //画像の読み込み
        const img = await loadImage(`item_image/${img_info.item_type}/repeat/${prefix}${img_info.item_data.name}${name_suffix}.${img_suffix}`);

        //item_data.width、item_data.heightはテクスチャの情報（CSV参照）
        //rect_widthは選択範囲横幅の実寸（入力値）
        const ratio = img_info.rect_width / img_info.item_data.width;
        const ratio2 = img_info.item_data.width / img_info.item_data.height;

        //img_info.widthはcanvas上の枠線の幅
        cv.width = img_info.width / ratio;
        cv.height = cv.width / ratio2;

        const context = cv.getContext('2d');

        //上下反転
        context.scale(1, -1);
        context.translate(0, -cv.height);

        //画面上での1リピート分のサイズでテクスチャを生成
        context.drawImage(img, 0, 0, img.width, img.height, 0, 0, cv.width, cv.height);

        return cv;

    } catch {
        cv.width = 500;
        cv.height = 500;
        const ctx = cv.getContext('2d');
        ctx.fillStyle = 'aqua';
        ctx.fillRect(0,0,cv.width,cv.height);
        return cv;
    }
}
async function createCanvasTexture(img_info, one_repeat, width_ratio = 1, height_ratio = 1) {
    const cv = document.createElement('canvas');
    cv.width = img_info.width * width_ratio;
    cv.height = img_info.height * height_ratio;

    //リピート回数を反映した全体のテクスチャを生成
    const ctx = cv.getContext('2d');
    ctx.beginPath();
    ctx.fillStyle = ctx.createPattern(one_repeat, 'repeat');
    ctx.rect(0,0,cv.width,cv.height);
    ctx.fill();

    return cv
}

//一山分ごとに分割したテクスチャを配列に入れる
//このテクスチャは後でスラット複製時にあてていく
async function createTextureArr(img_info, slat_height_ratio, texture_repeat_num, item_name, item_width) {
    const prefix = img_info.prefix ? `${img_info.prefix}/` : '';
    const slat_texture_url = `item_image/${img_info.item_type}/repeat/${prefix}${item_name}.jpg`;

    let slat_texture_arr = [];
    const img = await loadImage(slat_texture_url);

    //canvasでの画像描画は以下URL参考に
    //http://www.htmq.com/canvas/drawImage_s.shtml

    let is_centered_texture = false;
    let px;
    if(item_name === 'PS-2201') {
        is_centered_texture = true;
        px = makePxSettingObj(150, 150, 10, 183, 403);
    } else if(item_name === 'PS-2517') {
        is_centered_texture = true;
        px = makePxSettingObj(0, 63, 50, 58, 670);
    }

    if(is_centered_texture) {
        for (let i = 0; i < texture_repeat_num; i++) {
            slat_texture_arr.push(await createCenteredCanvasTexture(img_info, img, i, texture_repeat_num, slat_height_ratio, px, item_width));
        }
    } else {
        for(let i=0; i<texture_repeat_num; i++) {
            const one_repeat = await createCutCanvasOneRepeat(img_info, img, i, texture_repeat_num, slat_height_ratio, item_width);
            slat_texture_arr.push(await createCutCanvasTexture(img_info, one_repeat, slat_height_ratio));
        }
    }

    return slat_texture_arr;
}

function makePxSettingObj(bg_offset_x, bg_offset_y, sampling_size, main_img_offset, main_img_width) {
    return {bg_offset_x, bg_offset_y, sampling_size, main_img_offset, main_img_width};
}

async function createCenteredCanvasTexture(img_info, img, i, texture_repeat_num, slat_height_ratio, px, item_width) {
    const cv = document.createElement('canvas');

    //img_info.widthはcanvas上の枠線の幅
    cv.width = img_info.width;
    cv.height = img_info.height * slat_height_ratio;

    const cut_image_height = img.height / texture_repeat_num;
    const image_y_offset = cut_image_height * i;

    const context = cv.getContext('2d');

    //上下反転
    context.scale(1, -1);
    context.translate(0, -cv.height);

    //背景ワンリピート作り
    const bg_canvas = document.createElement('canvas');
    bg_canvas.width = px.sampling_size;
    bg_canvas.height = px.sampling_size;
    const bg_context = bg_canvas.getContext('2d');
    bg_context.drawImage(img, px.bg_offset_x, px.bg_offset_y, px.sampling_size, px.sampling_size, 0, 0, px.sampling_size, px.sampling_size);
    //背景を描画
    context.beginPath();
    context.fillStyle = context.createPattern(bg_canvas, 'repeat');
    context.rect(0, 0, cv.width, cv.height);
    context.fill();

    //中央の柄を描画
    const center_rate = px.main_img_width / img.width;
    const cv_main_width = cv.width * ((item_width * center_rate) / img_info.rect_width);
    const cv_offset_x = (cv.width - cv_main_width) / 2;
    context.drawImage(img, px.main_img_offset, image_y_offset, px.main_img_width, cut_image_height, cv_offset_x, 0, cv_main_width, cv.height);

    return cv;
}

async function createCutCanvasOneRepeat(img_info, img, i, texture_repeat_num, slat_height_ratio, item_width) {
    const cv = document.createElement('canvas');

    //item_data.width、item_data.heightはテクスチャの情報（CSV参照）
    //rect_widthは選択範囲横幅の実寸（入力値）
    const ratio = img_info.rect_width / item_width;

    //img_info.widthはcanvas上の枠線の幅
    cv.width = img_info.width / ratio;
    cv.height = img_info.height * slat_height_ratio;

    const cut_image_height = img.height / texture_repeat_num;
    const image_y_offset = cut_image_height * i;

    const context = cv.getContext('2d');

    //上下反転
    context.scale(1, -1);
    context.translate(0, -cv.height);

    //画面上での1リピート分のサイズでテクスチャを生成
    context.drawImage(img, 0, image_y_offset, img.width, cut_image_height, 0, 0, cv.width, cv.height);

    return cv;
}
async function createCutCanvasTexture(img_info, one_repeat, slat_height_ratio) {
    const cv = document.createElement('canvas');
    cv.width = img_info.width;
    cv.height = img_info.height * slat_height_ratio;

    //リピート回数を反映した全体のテクスチャを生成
    const ctx = cv.getContext('2d');
    ctx.beginPath();
    ctx.fillStyle = ctx.createPattern(one_repeat, 'repeat');
    ctx.rect(0,0,cv.width,cv.height);
    ctx.fill();

    return cv
}
